// frontend/src/SettingsPage.js
import React, { useState, useEffect, useCallback, useMemo, useId, useRef } from 'react';
import axios from 'axios';
import SettingsSidebar from './SettingsSidebar';
import './SettingsPage.css'; // Import the CSS file
import * as XLSX from 'xlsx';
import { v4 as uuidv4 } from 'uuid';
import ProtectedElement from './ProtectedElement';

// Base URL for API requests
const BASE_URL = process.env.REACT_APP_SERVER_URL || '';

const SettingsPage = ({ handleLogout, theme, setTheme })=> {
    // --- Default Settings and Load Logic ---
    const defaultSettings = useMemo(() => ({
        llmProvider: 'OpenAI',
        model: 'gpt-4o-mini',
        temperature: 0.5,
        maxTokens: 2500,
        url: 'https://api.openai.com/v1/chat/completions', // Default URL might get overwritten
    }), []);

    // --- Provider/Model Map ---
    // (Kept the same as provided previously)
    const providerModelMap = useMemo(() => ({
         OpenAI: [
             { name: 'gpt-4o-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '16000' },
             { name: 'gpt-4o', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '16000'  },
             { name: 'gpt-5-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '16000'  },
             { name: 'gpt-5-nano', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '32000'  },
             { name: 'gpt-5', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '32000'  },
             { name: 'gpt-4.1-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '16000'  },
             { name: 'gpt-4.1-nano', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '16000'  },
             { name: 'gpt-4.1', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '16000'  },
             { name: 'o3-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '128000'  },
             { name: 'o4-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '128000'  },
             { name: 'o1-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '128000'  },
         ],
         Anthropic: [
             { name: 'claude-3-5-sonnet-latest', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '8192'  },
             { name: 'claude-3-opus-latest', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '4096'  },
             { name: 'claude-3-sonnet-20240229', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '4096'  },
             { name: 'claude-3-haiku-20240307', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '4096'  },
         ],
         OpenSource: [
             { name: 'meta-llama/llama-4-scout-17b-16e-instruct', url: '', model_max_tokens: '131072'  },
             { name: 'deepseek-r1-distill-llama-70b', url: '', model_max_tokens: '128000'  },
             { name: 'llama-3.1-8b-instant', url: '', model_max_tokens: '128000'  },
             { name: 'meta-llama/llama-4-maverick-17b-128e-instruct', url: '', model_max_tokens: '131072'  },
         ],
         Grok: [
            { name: 'grok-3-mini', url: '', model_max_tokens: '131072'  },
            { name: 'grok-4', url: '', model_max_tokens: '131072'  },
            { name: 'grok-3', url: '', model_max_tokens: '131072'  },
        ],
         Local: [
             { name: 'llama3:latest', url: 'http://localhost:11434/api/chat', model_max_tokens: '8192' },
         ]
     }), []);

    // (Function loadSettingsFromLocalStorage kept the same as provided previously)
    const loadSettingsFromLocalStorage = useCallback(() => {
        const savedSettings = localStorage.getItem('llmSettings');
        if (savedSettings) {
            try {
                const parsed = JSON.parse(savedSettings);
                // Validate provider
                if (!parsed.llmProvider || !providerModelMap[parsed.llmProvider]) {
                     console.warn("Invalid provider in saved settings, reverting to default.");
                     return defaultSettings;
                }
                const providerModels = providerModelMap[parsed.llmProvider];
                // Find selected model within the valid provider's models
                const selectedModel = providerModels?.find(m => m.name === parsed.model);

                if (selectedModel) {
                    // Update URL from the found model details
                    parsed.url = selectedModel.url;
                } else if (providerModels && providerModels[0]) {
                    // If saved model doesn't exist for the provider, use the first available model
                    console.warn(`Saved model '${parsed.model}' not found for provider '${parsed.llmProvider}', using default '${providerModels[0].name}'.`);
                    parsed.model = providerModels[0].name;
                    parsed.url = providerModels[0].url;
                } else {
                    // If provider has no models listed (or provider was invalid earlier), revert fully
                    console.warn(`No models found for provider '${parsed.llmProvider}', reverting to default settings.`);
                    return defaultSettings;
                }

                // Validate and clamp temperature
                parsed.temperature = typeof parsed.temperature === 'number' && !isNaN(parsed.temperature)
                                      ? Math.max(0, Math.min(2, parsed.temperature))
                                      : defaultSettings.temperature;

                // Validate and clamp maxTokens based on the potentially updated model
                parsed.maxTokens = typeof parsed.maxTokens === 'number' && !isNaN(parsed.maxTokens)
                                      ? Math.max(1, parsed.maxTokens) // Ensure at least 1
                                      : defaultSettings.maxTokens;

                const finalSelectedModel = providerModels?.find(m => m.name === parsed.model); // Re-check model details
                const modelMax = finalSelectedModel ? parseInt(finalSelectedModel.model_max_tokens, 10) : null;
                if (modelMax && !isNaN(modelMax)) {
                    parsed.maxTokens = Math.min(parsed.maxTokens, modelMax); // Clamp to model max
                }

                // Merge parsed (and validated) settings with defaults
                return { ...defaultSettings, ...parsed };
            } catch (e) {
                 console.error("Error parsing settings from localStorage:", e);
                 return defaultSettings; // Fallback to defaults on parse error
            }
        }
        return defaultSettings; // Return defaults if no settings saved yet
    }, [defaultSettings, providerModelMap]);

    // --- State Definitions ---
    const [settings, setSettings] = useState(loadSettingsFromLocalStorage);
    const [prompts, setPrompts] = useState([]);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [activeTab, setActiveTab] = useState('Main');
    const [pipelinePrompts, setPipelinePrompts] = useState([]);
    const [pipelines, setPipelines] = useState([]);
    const [selectedPipeline, setSelectedPipeline] = useState(null);
    const [maxTokenValue, setMaxTokenValue] = useState(() => {
         const currentProviderModels = providerModelMap[settings.llmProvider];
         const currentModel = currentProviderModels?.find(model => model.name === settings.model);
         return currentModel ? parseInt(currentModel.model_max_tokens, 10) || 16000 : 16000; // Default fallback
    });
    const [promptsLoading, setPromptsLoading] = useState(false);
    const [pipelinesLoading, setPipelinesLoading] = useState(false);
    const [apiError, setApiError] = useState(null);
    // --- NEW: Loading state for saving settings ---
    const [isSaving, setIsSaving] = useState(false);

    // --- API Error Handling (kept the same) ---
     const handleApiError = useCallback((error, context) => {
        console.error(`Error ${context}:`, error);
        if (error.response.data.error === "Permission denied.") {
            // Suppress error message for permission issues
            console.log("Suppressing permission denied error");
            return;
        }
        let errorMessage = `An error occurred while ${context}.`;
        if (error.response) {
            // Use error message from backend response if available
            errorMessage = error.response.data?.error || error.response.data?.message || `Server error ${error.response.status} during ${context}.`;
            // Handle authentication errors specifically
            if (error.response.status === 401 && handleLogout) {
                console.log("Unauthorized error detected, attempting logout.");
                setApiError("Session expired or invalid. Please log in again.");
                setTimeout(() => handleLogout(), 1500); // Logout after delay
                return; // Stop further processing
            }
        } else if (error.request) {
            errorMessage = `No response received while ${context}. Check network/server.`;
        } else {
            errorMessage = `Error setting up request for ${context}: ${error.message}`;
        }
        setApiError(errorMessage);
        // Automatically clear the error message after a delay
        setTimeout(() => setApiError(null), 7000); // 7 seconds
    }, [handleLogout]);

    // --- Data Fetching (kept the same) ---
    const refreshPrompts = useCallback(() => {
        setPromptsLoading(true);
        setApiError(null);
        axios.get('/api/prompts', { withCredentials: true })
            .then(response => {
                setPrompts(Array.isArray(response.data) ? response.data : []);
            })
            .catch(error => handleApiError(error, 'fetching prompts'))
            .finally(() => setPromptsLoading(false));
    }, [handleApiError]);

    const getModelDetails = (provider, modelName, providerModelMap) => {
        if (!provider || !modelName || !providerModelMap[provider]) {
            return null;
        }
        return providerModelMap[provider].find(m => m.name === modelName) || null;
    };


    const refreshPipelines = useCallback(() => {
        setPipelinesLoading(true);
        setApiError(null);
        axios.get('/api/prompt-pipelines/get-pipelines', { withCredentials: true })
            .then(response => {
                // Backend now returns { id, name, username } for each pipeline
                setPipelines(Array.isArray(response.data) ? response.data : []);
                console.log("Fetched pipelines (with username):", response.data);
            })
            .catch(error => handleApiError(error, 'fetching pipelines list'))
            .finally(() => setPipelinesLoading(false));
    }, [handleApiError]); // Ensure handleApiError is stable or included if it changes

    useEffect(() => { refreshPrompts(); refreshPipelines(); }, [refreshPrompts, refreshPipelines]);

    // --- Update max token limit based on selected model (kept the same) ---
    useEffect(() => {
        const currentProviderModels = providerModelMap[settings.llmProvider];
        const currentModel = currentProviderModels?.find(model => model.name === settings.model);
        let newMaxTokenLimit = 16000; // Default fallback
        let modelUrl = settings.url; // Keep current URL initially

        if (currentModel) {
            const modelMax = parseInt(currentModel.model_max_tokens, 10);
            if (!isNaN(modelMax) && modelMax > 0) {
                newMaxTokenLimit = modelMax;
            } else {
                console.warn(`Invalid or missing model_max_tokens for ${settings.model}, using default ${newMaxTokenLimit}.`);
            }
            modelUrl = currentModel.url; // Update URL based on selected model
        } else if (settings.llmProvider && settings.model) {
            // Handle case where model details might be missing
            console.warn(`Model details not found for ${settings.llmProvider} - ${settings.model}. Using default max token limit.`);
        }

        setMaxTokenValue(newMaxTokenLimit);

        // Update settings state only if necessary (clamp maxTokens or update URL)
        setSettings(prevSettings => {
             const needsUpdate = prevSettings.maxTokens > newMaxTokenLimit || prevSettings.url !== modelUrl;
             if (needsUpdate) {
                  return { ...prevSettings, maxTokens: Math.min(prevSettings.maxTokens, newMaxTokenLimit), url: modelUrl };
             }
             return prevSettings; // No change needed
        });
    }, [settings.llmProvider, settings.model, providerModelMap, settings.url]); // Added settings.url dependency

    // --- Event Handlers (most kept the same) ---
    const handleSettingChange = useCallback((key, value) => {
        setSettings(prevSettings => {
            let newSettings = { ...prevSettings };
            if (key === 'llmProvider') {
                const firstModel = providerModelMap[value]?.[0];
                if (firstModel) {
                    const modelMax = parseInt(firstModel.model_max_tokens, 10) || 16000;
                    newSettings = { ...newSettings, [key]: value, model: firstModel.name, url: firstModel.url, maxTokens: Math.min(prevSettings.maxTokens, modelMax) };
                } else {
                    // Handle case where selected provider might have no models listed (unlikely with map)
                    newSettings = { ...newSettings, [key]: value, model: '', url: '' };
                }
            } else if (key === 'model') {
                const providerModels = providerModelMap[newSettings.llmProvider];
                const selectedModel = providerModels?.find(m => m.name === value);
                if (selectedModel) {
                    const modelMax = parseInt(selectedModel.model_max_tokens, 10) || 16000;
                    newSettings = { ...newSettings, [key]: value, url: selectedModel.url, maxTokens: Math.min(prevSettings.maxTokens, modelMax) };
                } else {
                    // Model not found (shouldn't happen if dropdown is populated correctly)
                    newSettings = { ...newSettings, [key]: value };
                }
            } else if (key === 'temperature') {
                const numericValue = parseFloat(value);
                 if (!isNaN(numericValue)) {
                      newSettings = { ...newSettings, [key]: Math.max(0, Math.min(2, numericValue)) };
                 }
            } else if (key === 'maxTokens') {
                const numericValue = parseInt(value, 10);
                 if (!isNaN(numericValue)) {
                    // Use the state value maxTokenValue for clamping
                    const cappedValue = Math.min(numericValue, maxTokenValue || 1);
                    newSettings = { ...newSettings, [key]: Math.max(1, cappedValue) };
                 }
            } else {
                 // Handle other potential settings if added later
                 newSettings = { ...newSettings, [key]: value };
            }
            return newSettings;
        });
    }, [providerModelMap, maxTokenValue]); // Added maxTokenValue dependency

    const handleSliderChange = useCallback((key, value) => {
        handleSettingChange(key, value);
    }, [handleSettingChange]);

    // --- Prompt & Pipeline Handlers (kept the same) ---
    const handleSelectPromptChange = useCallback((event) => {
        const promptId = event.target.value;
        setSelectedPrompt(prompts.find(p => p.id.toString() === promptId) || null);
    }, [prompts]);

    const handleAddToPipeline = useCallback(() => {
        if (selectedPrompt && !pipelinePrompts.some(p => p.id === selectedPrompt.id)) {
            setPipelinePrompts(current => [...current, selectedPrompt]);
        } else if (!selectedPrompt) {
             alert("Please select a prompt from the dropdown first.");
        } else {
             alert("This prompt is already in the current pipeline.");
        }
    }, [selectedPrompt, pipelinePrompts]);

    const handleRemoveFromPipeline = useCallback((promptToRemove) => {
        setPipelinePrompts(current => current.filter(p => p.id !== promptToRemove.id));
    }, []);

    const handleSavePipeline = useCallback(async (name, promptIds) => {
        if (!name.trim()) { alert("Please enter a name for the pipeline."); return; }
        if (promptIds.length === 0) { alert("Please add at least one prompt to the current pipeline."); return; }
        setApiError(null);
        // Create new pipeline, backend associates with current user
        try {
            await axios.post('/api/prompt-pipelines/create', { name: name.trim(), prompts: promptIds }, { withCredentials: true });
            alert('New pipeline saved successfully!');
            setPipelinePrompts([]); 
            setSelectedPrompt(null); // Ensure prompt selection dropdown is also reset
            setSelectedPipeline(null); // Clear selected pipeline for editor
            refreshPipelines(); 
        } catch (error) {
            handleApiError(error, 'saving new pipeline');
        }
    }, [refreshPipelines, handleApiError]);

    const handleSelectPipelineChange = useCallback(async (event) => {
        const pipelineId = event.target.value;
        setApiError(null);

        if (!pipelineId) {
            setSelectedPipeline(null);
            setPipelinePrompts([]); // Clear editor list
            return;
        }

        const selectedPipelineSummary = pipelines.find(p => p.id.toString() === pipelineId);
        if (!selectedPipelineSummary) {
            console.error(`Pipeline with ID ${pipelineId} not found in local list.`);
            setSelectedPipeline(null);
            setPipelinePrompts([]);
            return;
        }
        
        // Store the summary (id, name, username) immediately for UI responsiveness
        setSelectedPipeline(selectedPipelineSummary); 
        setPipelinesLoading(true); // Indicate loading pipeline details

        try {
            // Fetch full pipeline details (which includes the 'prompts' array of IDs and username)
            const response = await axios.get(`/api/prompt-pipelines/${pipelineId}`, { withCredentials: true });
            const pipelineDetails = response.data; // Expects { id, name, prompts: [...ids], username }

            if (pipelineDetails && Array.isArray(pipelineDetails.prompts)) {
                // Map prompt IDs from details to full prompt objects from the fetched 'prompts' state
                const orderedPrompts = pipelineDetails.prompts
                    .map(id => prompts.find(p => p.id === id || p.id.toString() === id.toString())) // Handle number/string ID comparison
                    .filter(Boolean); // Filter out any prompts not found (IDs might be stale)
                
                setPipelinePrompts(orderedPrompts); // Update the editor list with full prompt objects

                // Update selectedPipeline state with full details, including the prompt objects for consistency
                // and the username from the detailed fetch.
                setSelectedPipeline({
                    id: pipelineDetails.id,
                    name: pipelineDetails.name,
                    username: pipelineDetails.username, // Store the owner's username
                    prompts: orderedPrompts // Store full prompt objects, not just IDs
                });
                 console.log("Selected pipeline details loaded:", {
                    id: pipelineDetails.id,
                    name: pipelineDetails.name,
                    username: pipelineDetails.username,
                    promptCount: orderedPrompts.length
                });

            } else {
                console.error('Invalid pipeline details format:', pipelineDetails);
                setPipelinePrompts([]);
                setApiError('Failed to load pipeline details: Invalid data format.');
                // Revert selectedPipeline to summary if full details fail
                setSelectedPipeline(selectedPipelineSummary); 
            }
        } catch (error) {
            handleApiError(error, `fetching pipeline details for ${selectedPipelineSummary.name}`);
            setPipelinePrompts([]);
            // Revert selectedPipeline to summary if full details fail
            setSelectedPipeline(selectedPipelineSummary); 
        } finally {
            setPipelinesLoading(false);
        }
    }, [pipelines, prompts, handleApiError]);

    const clearPipelineEditor = useCallback(() => {
        console.log("clearPipelineEditor called");
        setSelectedPipeline(null);
        setPipelinePrompts([]);
        setSelectedPrompt(null); 
    }, [setSelectedPipeline, setPipelinePrompts, setSelectedPrompt]); // Using setters which are stable

    const handleUpdatePipeline = useCallback(async (pipelineId, name, promptIds) => {
        if (!pipelineId) { alert("Cannot update pipeline: ID missing."); return; }
        if (!name.trim()) { alert("Pipeline name cannot be empty."); return; }
        if (promptIds.length === 0) { alert("Pipeline must contain at least one prompt."); return; }
        setApiError(null);

        // The backend PUT /api/prompt-pipelines/<id> handles:
        // 1. If system pipeline: Creates a new pipeline for the current user.
        // 2. If user's pipeline: Updates it.
        // 3. If another user's pipeline: Denies.
        try {
            const response = await axios.put(
                `/api/prompt-pipelines/update/${pipelineId}`,
                { name: name.trim(), prompts: promptIds },
                { withCredentials: true }
            );
            
            alert('Pipeline action processed successfully!'); // Generic message as backend handles new vs update
            refreshPipelines(); 

            // If a new pipeline was created (status 201), or existing updated (status 200)
            // The response.data will be the pipeline object.
            // We can use this to potentially update the selectedPipeline state
            // or simply clear the editor to reflect the list being refreshed.
            if (response.status === 201) { // Created new (from system pipeline)
                console.log("System pipeline saved as new user pipeline:", response.data);
                // Optionally, select the newly created pipeline
                // setSelectedPipeline(response.data); 
                // For simplicity, let's clear and let user re-select if needed
                 clearPipelineEditor();
            } else if (response.status === 200) { // Updated existing
                console.log("User pipeline updated:", response.data);
                 // If still editing the same one, update its details in the editor
                 if (selectedPipeline && selectedPipeline.id === response.data.id) {
                    // Re-fetch the details to be absolutely sure state is consistent
                    // Or, if confident, parse response.data.prompts (list of IDs)
                    // and map them to full prompt objects for setPipelinePrompts
                    // For now, a simpler approach is to clear the editor or re-select.
                    // Let's re-select the updated pipeline to refresh editor.
                    const updatedPipelineSummary = {
                        id: response.data.id,
                        name: response.data.name,
                        username: response.data.username
                    };
                    setSelectedPipeline(updatedPipelineSummary);
                    // Trigger a re-fetch of its full details to update `pipelinePrompts`
                    const event = { target: { value: response.data.id.toString() } };
                    await handleSelectPipelineChange(event);
                 } else {
                    clearPipelineEditor();
                 }
            }
        } catch (error) {
            handleApiError(error, 'updating pipeline');
        }
    }, [refreshPipelines, handleApiError, selectedPipeline, clearPipelineEditor, handleSelectPipelineChange]);

const handleUpdatePrompt = useCallback((promptId, name, text, group, llmSettings, toolSettings, collectionsPayload) => {
    if (!promptId) { alert('Cannot update prompt: ID missing.'); return; }
    if (!name.trim()) { alert('Prompt name cannot be empty.'); return; }
    if (!text.trim()) { alert('Prompt text cannot be empty.'); return; }
    setApiError(null);

    const payload = {
        name: name.trim(),
        text: text.trim(),
        group: group ? group.trim() : null, // Send group, or null if empty
        // Conditionally spread llmSettings if they are provided and custom settings are used
        // Assuming llmSettings contains { provider, model, temperature, max_tokens }
        // and these can be null if not using custom settings.
        provider: llmSettings.provider,
        model: llmSettings.model,
        temperature: llmSettings.temperature,
        max_tokens: llmSettings.max_tokens,
        tools: toolSettings.tools, // Add the tools payload
        tool_discovery: toolSettings.tool_discovery,
    };

    axios.put(`/api/prompts/update/${promptId}`, payload, { withCredentials: true })
    .then(async (response) => {
        // After a successful prompt update, update its collections if provided
        try {
            if (collectionsPayload && Array.isArray(collectionsPayload.collection_ids)) {
                const ids = collectionsPayload.collection_ids
                    .map((cid) => parseInt(cid, 10))
                    .filter((n) => Number.isInteger(n));
                await axios.post(`/api/prompts/${promptId}/collections`, { collection_ids: ids }, { withCredentials: true });
            }
        } catch (err) {
            handleApiError(err, 'updating prompt collections');
        }

        alert("Prompt updated successfully!");
        refreshPrompts(); // Refresh prompt list
        // Update the selected prompt state to reflect the change immediately
        // Backend should return the updated prompt object
        setSelectedPrompt(response.data);
    })
    .catch(error => handleApiError(error, 'updating prompt'));
}, [refreshPrompts, handleApiError]);

const handleCreatePrompt = useCallback((name, text, group, llmSettings, toolSettings, collectionsPayload) => {
    if (!name.trim()) { alert('Please enter a name for the new prompt.'); return; }
    if (!text.trim()) { alert('Please enter the text for the new prompt.'); return; }
    setApiError(null);

   const payload = {
       name: name.trim(),
       text: text.trim(),
       group: group ? group.trim() : null, // Send group, or null if empty
       // Conditionally spread llmSettings
       provider: llmSettings.provider,
       model: llmSettings.model,
       temperature: llmSettings.temperature,
       max_tokens: llmSettings.max_tokens,
       tools: toolSettings.tools, // Add the tools payload
       tool_discovery: toolSettings.tool_discovery,
   };

   axios.post('/api/prompts/create', payload, { withCredentials: true })
   .then(async (response) => {
       // After a successful prompt creation, set its collections using the returned id
       try {
           const newPrompt = response?.data;
           const newId = newPrompt?.id;
           if (newId && collectionsPayload && Array.isArray(collectionsPayload.collection_ids)) {
               const ids = collectionsPayload.collection_ids
                   .map((cid) => parseInt(cid, 10))
                   .filter((n) => Number.isInteger(n));
               await axios.post(`/api/prompts/${newId}/collections`, { collection_ids: ids }, { withCredentials: true });
           }
       } catch (err) {
           handleApiError(err, 'setting collections for new prompt');
       }

       alert("New prompt created successfully!");
       refreshPrompts(); // Refresh list
       // Clear the editor fields by resetting selectedPrompt
       setSelectedPrompt(null); // This will also trigger the useEffect in PromptManagementContent to clear fields
   })
   .catch(error => handleApiError(error, 'creating new prompt'));
}, [refreshPrompts, handleApiError]);

    // --- NEW: Updated handleSaveSettings ---
    const handleSaveSettings = useCallback(async () => {
        setApiError(null); // Clear previous errors
        setIsSaving(true); // Show loading state

        // Prepare the data payload matching backend expectations
        const settingsPayload = {
            llmProvider: settings.llmProvider,
            model: settings.model,
            temperature: settings.temperature,
            maxTokens: settings.maxTokens,
        };

        console.log("Attempting to save settings:", settingsPayload);

        try {
            // Make the API call to the new backend endpoint
            const response = await axios.post('/api/user/settings', settingsPayload, {
                withCredentials: true, // Send session cookies
            });

            // If API call is successful, save to local storage (optional persistence)
            localStorage.setItem('llmSettings', JSON.stringify(settings));
            alert(response.data.message || 'Settings saved successfully!'); // Provide success feedback from backend

        } catch (error) {
            // Use the centralized error handler to display API errors
            handleApiError(error, 'saving settings');
            // Fallback alert if handleApiError doesn't cover it (e.g., network error before response)
            if (!error.response) {
                 alert('Failed to save settings. Check network connection or server status.');
            }
        } finally {
            setIsSaving(false); // Hide loading state regardless of success/failure
        }
    }, [settings, handleApiError]); // Dependencies: settings state and error handler

    // --- Clear State Handlers (kept the same) ---
    const clearSelectedPrompt = useCallback(() => {
        setSelectedPrompt(null);
    }, []);
   

    const handleDeletePrompt = useCallback(async () => {
        if (!selectedPrompt || !selectedPrompt.id) {
            alert("Please select a prompt to delete.");
            return;
        }
        // User confirmation
        if (!window.confirm(`Are you sure you want to delete the prompt "${selectedPrompt.prompt_name}"? This action cannot be undone.`)) {
            return;
        }
    
        setApiError(null); // Clear previous errors
        // Optional: Add a loading state for delete if you have one, e.g., setIsDeleting(true);
        try {
            await axios.delete(`/api/prompts/delete/${selectedPrompt.id}`, { withCredentials: true });
            alert(`Prompt "${selectedPrompt.prompt_name}" deleted successfully!`);
            refreshPrompts(); // Refresh the list of prompts from the server
            setSelectedPrompt(null); // Clear the selection and reset the form fields
                                     // This will also trigger the useEffect in PromptManagementContent
                                     // to clear its internal form state.
        } catch (error) {
            // Use the existing handleApiError to display the error
            handleApiError(error, `deleting prompt "${selectedPrompt.prompt_name}"`);
        } finally {
            // Optional: setIsDeleting(false);
        }
    }, [selectedPrompt, refreshPrompts, handleApiError, setSelectedPrompt]);

    const handleDeletePipeline = useCallback(async () => {
        console.log("--- handleDeletePipeline (SettingsPage.js) ---"); // Log 1
    
        if (!selectedPipeline || !selectedPipeline.id) {
            alert("Please select a pipeline to delete.");
            console.warn("handleDeletePipeline: ABORT - No pipeline selected or ID missing. Current selectedPipeline:", selectedPipeline); // Log 2
            return;
        }
    
        console.log("handleDeletePipeline: Pipeline selected:", selectedPipeline); // Log 2.1: Log the full selected pipeline object
    
        // This check is important. The button should ideally not even be visible for system pipelines.
        if (selectedPipeline.username === null || selectedPipeline.username === '') {
            alert("System pipelines cannot be deleted directly. You can 'Save as New Copy' to create your own version.");
            console.warn("handleDeletePipeline: ABORT - Attempt to delete a system pipeline. Username:", selectedPipeline.username); // Log 3
            return;
        }
    
        if (!window.confirm(`Are you sure you want to delete your pipeline "${selectedPipeline.name}"? This action cannot be undone.`)) {
            console.log("handleDeletePipeline: User cancelled deletion."); // Log 4
            return;
        }
    
        setApiError(null);
        console.log(`handleDeletePipeline: CONFIRMED - Proceeding to delete pipeline ID: ${selectedPipeline.id}, Name: "${selectedPipeline.name}", Owner: "${selectedPipeline.username}"`); // Log 5
    
        try {
            const response = await axios.delete(`/api/prompt-pipelines/delete/${selectedPipeline.id}`, { withCredentials: true });
            
            console.log("handleDeletePipeline: Delete API call successful. Response status:", response.status, "Response data:", response.data); // Log 6
            alert(`Pipeline "${selectedPipeline.name}" deleted successfully!`);
            
            refreshPipelines();      // Refresh the list from the server
            clearPipelineEditor();   // Clear the editor and selection
            
        } catch (error) {
            console.error("handleDeletePipeline: Error during delete API call or subsequent actions.", error); // Log 7
            if (error.response) {
                console.error("handleDeletePipeline: Error response data:", error.response.data);
                console.error("handleDeletePipeline: Error response status:", error.response.status);
            }
            handleApiError(error, `deleting pipeline "${selectedPipeline.name}"`);
        }
    }, [selectedPipeline, refreshPipelines, handleApiError, clearPipelineEditor]);

    // Memoize the component type so it isn't recreated on every SettingsPage render.
    // Recreating the component can cause effects to re-run and spam `/api/directories`.
    const FileUploadHandler = useMemo(() => React.memo(({ prompts, handleApiError, promptsLoading }) => {
        // --- Hooks called at the top level ---
        const uploadSectionIdBase = useId();
        const processSectionIdBase = useId();

        // --- States (FileUploadHandler specific) ---
        const [files, setFiles] = useState([]);
        const [isSelectingDirectory, setIsSelectingDirectory] = useState(false);
        const [directoryName, setDirectoryName] = useState('');
        const [uploadLoading, setUploadLoading] = useState(false);
        const [uploadSuccessMessage, setUploadSuccessMessage] = useState('');
        const [uploadErrorMessage, setUploadErrorMessage] = useState('');
        const [uploadedFileNames, setUploadedFileNames] = useState([]);
        const [unsupportedFileNames, setUnsupportedFileNames] = useState([]);
        // New states for partial upload handling
        const [successfulUploadFiles, setSuccessfulUploadFiles] = useState([]); // array of names
        const [failedUploadFiles, setFailedUploadFiles] = useState([]); // array of names
        const [partialUpload, setPartialUpload] = useState(false);
        const [selectedSummaryPromptUpload, setSelectedSummaryPromptUpload] = useState(null);
        const [selectedExtractionPromptUpload, setSelectedExtractionPromptUpload] = useState(null);
        const [processingFlag, setProcessingFlag] = useState(false);
        const [fullVision, setFullVision] = useState(false);
        const [processLoading, setProcessLoading] = useState(false);
        const [processSuccessMessage, setProcessSuccessMessage] = useState('');
        const [processErrorMessage, setProcessErrorMessage] = useState('');
        const [allFiles, setAllFiles] = useState([]);
        const [selectedFilesForProcessing, setSelectedFilesForProcessing] = useState([]);
        const [searchTerm, setSearchTerm] = useState("");
        const [fetchFilesLoading, setFetchFilesLoading] = useState(false);
        const [allDirectories, setAllDirectories] = useState([]);
        const [fetchDirsLoading, setFetchDirsLoading] = useState(false);
        const [selectedSummaryPromptProcess, setSelectedSummaryPromptProcess] = useState(null);
        const [selectedExtractionPromptProcess, setSelectedExtractionPromptProcess] = useState(null);
        const [jobProgress, setJobProgress] = useState(null);
        const [jobId, setJobId] = useState(null);
        const [jobStartTime, setJobStartTime] = useState(null); // Track when the job started
        const [elapsedTime, setElapsedTime] = useState(0); // Track elapsed

        const initialLoadCompleteRef = useRef(false);

        const findPromptById = useCallback((promptsList, id) => {
            if (!Array.isArray(promptsList) || !id) return undefined;
            const targetId = String(id);
            return promptsList.find(prompt => String(prompt.id) === targetId);
         }, []);
        
        const summaryPrompts = Array.isArray(prompts) ? prompts.filter(prompt => prompt.group === 'summary') : [];
        const extractionPrompts = Array.isArray(prompts) ? prompts.filter(prompt => prompt.group === 'extraction') : [];

        // --- API Fetch Utility (FileUploadHandler specific) ---
        const fetchData = useCallback(async (url, params = {}, setLoadingState, context) => {
            try {
                const response = await axios.get(url, { params, withCredentials: true });
                // Parse based on expected endpoint structure
                if (url.includes('/api/directories')) {
                     return Array.isArray(response.data.directories) ? response.data.directories : [];
                } else if (url.includes('/api/all_files_sorted') || url.includes('/api/search_files')) {
                    const dataKey = url.includes('/api/search_files') ? 'results' : 'files';
                    return Array.isArray(response.data?.[dataKey]) ? response.data[dataKey] : [];
                }
                return response.data || [];
            } catch (error) {
                 handleApiError(error, `fetching ${context || 'data'}`);
                 return [];
            }
        }, [handleApiError]);

        const formatElapsedTime = useCallback((seconds) => {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = seconds % 60;
            
            if (hours > 0) {
                return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            } else {
                return `${minutes}:${secs.toString().padStart(2, '0')}`;
            }
        }, []);

        // --- Effects (FileUploadHandler specific) ---
        useEffect(() => {
            if (initialLoadCompleteRef.current) return;
            // Mark immediately to prevent duplicate in-flight loads if dependencies change
            // (e.g. parent recreates handlers) while the first request is still running.
            initialLoadCompleteRef.current = true;

            const loadInitialData = async () => {
                try {
                    setFetchDirsLoading(true);
                    const fetchedDirs = await fetchData('/api/directories', {}, () => {}, 'directories');
                    setAllDirectories(fetchedDirs);
                    setFetchDirsLoading(false);

                    // Only fetch initial files if search term is empty
                    if (!searchTerm.trim()) {
                        setFetchFilesLoading(true);
                        const fetchedFiles = await fetchData('/api/all_files_sorted', { limit: 50, offset: 0 }, () => {}, 'initial files');
                        setAllFiles(fetchedFiles);
                        setFetchFilesLoading(false);
                    }

                } catch (error) {
                    console.error('Error in initial data load:', error);
                    setFetchDirsLoading(false);
                    setFetchFilesLoading(false);
                    initialLoadCompleteRef.current = false;
                }
            };

            loadInitialData();
        }, [fetchData, searchTerm]);

        useEffect(() => {
            let intervalId;
            let timerIntervalId;

            if (jobId) {
                const pollOnce = async () => {
                    try {
                        const res = await axios.get(`/api/job-processing-progress/${jobId}`, { withCredentials: true });
                        setJobProgress(res.data);
                        
                        // Set start time when we first get it from the backend
                        if (res.data.startedAt && !jobStartTime) {
                            setJobStartTime(new Date(res.data.startedAt));
                        }

                        if (res.data.status === "COMPLETED" || res.data.status === "PARTIAL") {
                            clearInterval(intervalId);
                            clearInterval(timerIntervalId);
                            localStorage.removeItem('currentJobId'); // Clean up when done
                            // setJobStartTime(null);
                        }
                        if (res.data.status === "FAILED") {
                            clearInterval(intervalId);
                            clearInterval(timerIntervalId);
                            setJobProgress(null);
                            setJobId(null);
                            setJobStartTime(null); 
                            setElapsedTime(0);
                            localStorage.removeItem('currentJobId');
                            setUploadErrorMessage(`Job failed: ${res.data.error || 'Unknown error'}`);
                        }
                    } catch (err) {
                        console.error("Error polling job progress:", err);
                    }
                };

                // Poll immediately so the UI can render progress without waiting 5 seconds.
                pollOnce();
                intervalId = setInterval(pollOnce, 5000); // Poll every 5 seconds

                // Update timer every second
                timerIntervalId = setInterval(() => {
                    if (jobStartTime) {
                        const now = new Date();
                        const elapsed = Math.floor((now - jobStartTime) / 1000); // seconds
                        setElapsedTime(elapsed);
                    }
                }, 1000);
            }

            return () => {
                clearInterval(intervalId);
                clearInterval(timerIntervalId);
            };
        }, [jobId, jobStartTime]);

        useEffect(() => {
            // On mount, check for an unfinished job
            const storedJobId = localStorage.getItem('currentJobId');
            if (storedJobId) {
                setJobId(storedJobId);
            }
        }, []);

        // --- Handlers (FileUploadHandler specific - kept same logic) ---
        const handleFileChange = useCallback((event) => {
            const selectedFilesArray = Array.from(event.target.files || []);
            // Supported types based on backend processing capabilities
            const supportedExts = new Set(['.pdf', '.docx', '.txt', '.csv', '.png', '.jpg', '.jpeg', '.pptx']);
            const isSupported = (name) => {
                const dot = name.lastIndexOf('.');
                if (dot === -1) return false;
                const ext = name.slice(dot).toLowerCase();
                return supportedExts.has(ext);
            };

            const supported = selectedFilesArray.filter(f => isSupported(f.name));
            const unsupported = selectedFilesArray.filter(f => !isSupported(f.name));

            setFiles(supported);
            setUploadedFileNames(supported.map(f => f.name));
            setUnsupportedFileNames(unsupported.map(f => f.name));
            setUploadSuccessMessage('');
            setUploadErrorMessage('');
        }, []);

        const handleDirectoryInputChange = useCallback((event) => {
            setDirectoryName(event.target.value);
            setUploadErrorMessage(''); // Clear error on change
        }, []);

        const setDirectoryModeNew = useCallback(() => {
            setIsSelectingDirectory(false);
            setDirectoryName(''); 
            setUploadErrorMessage('');
        }, []);

        const setDirectoryModeSelect = useCallback(() => {
            setIsSelectingDirectory(true);
            setDirectoryName(''); // Reset directory name when toggling
            setUploadErrorMessage(''); // Clear potential errors
        }, []);

        const handleSearch = useCallback(async () => {
            setSelectedFilesForProcessing([]); // Clear selection on new search
            setProcessSuccessMessage(''); 
            setProcessErrorMessage(''); // Clear processing messages
            setUploadErrorMessage('');

            const currentSearchTerm = searchTerm.trim();
            setFetchFilesLoading(true);

            let results = [];
            if (currentSearchTerm) {
                results = await fetchData('/api/search_files', { searchTerm: currentSearchTerm });
            } else {
                // Fetch initial sorted list if search is cleared
                results = await fetchData('/api/all_files_sorted', { limit: 50, offset: 0 });
            }

            setAllFiles(results);
            setFetchFilesLoading(false);
        }, [searchTerm, fetchData]);

        const handleFileCheckboxChange = useCallback((file) => {
            setSelectedFilesForProcessing(prev =>
                prev.some(f => f.id === file.id)
                    ? prev.filter(f => f.id !== file.id)
                    : [...prev, file]
            );
        }, []);

        const handleSelectAllToggle = useCallback(() => {
            if (allFiles.length === 0) return;
            setSelectedFilesForProcessing(prev =>
                prev.length === allFiles.length ? [] : [...allFiles]
            );
        }, [allFiles]);

        const handleFileUpload = useCallback(async () => {
            // Input validation
            if (files.length === 0 || !directoryName.trim()) {
                setUploadErrorMessage('Please enter a directory name and select files.');
                return;
            }
        
            const syncJobId = uuidv4(); // Unique ID for the upload job, used for tracking progress

            // let the user know of estimated costs
            try {
                // get the file sizes
                const fileSizes = files.map(file => ({
                    name: file.name,
                    size: file.size,
                    sizeInMB: (file.size / (1024 * 1024)).toFixed(2), // Convert to MB
                    asyncProcessing: processingFlag,
                    fullVision: fullVision
                }));
                // Make a call to the api to estimate costs:
                const costEstimateResponse = await axios.post('/api/estimate_upload', fileSizes, {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true
                });
                // Display the estimated cost to the user in a confirmation dialog
                const estimatedCost = parseFloat(costEstimateResponse.data.estimated_cost_usd);
                const estimatedTime = parseFloat(costEstimateResponse.data.estimated_time_seconds);
                const minutes = Math.floor(estimatedTime / 60);
                const seconds = Math.ceil(estimatedTime % 60);
                const displayTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;
                const confirmationMessage = `Estimated cost for this upload: $${estimatedCost.toFixed(2)}\nEstimated processing time: ${displayTime}.\n\nDo you want to proceed with the upload?`;
                if (!window.confirm(confirmationMessage)) {
                    setUploadLoading(false);
                    setUploadErrorMessage('Upload cancelled by user.');
                    return; // User cancelled, stop the upload
                }
            } catch (costEstimateError) {
                console.error("Cost estimation failed:", costEstimateError);
                setUploadErrorMessage("Could not estimate upload costs (see console), continuing upload without cost estimate.");
                // Proceed with upload even if cost estimation fails
            }

            // Prepare form data
            const formData = new FormData();
            formData.append('directory', directoryName.trim());
            formData.append('customPromptSummaryID', selectedSummaryPromptUpload?.id || "");
            formData.append('customPromptExtractionID', selectedExtractionPromptUpload?.id || "");
            formData.append('processingFlag', processingFlag);
            formData.append('fullVision', fullVision ? 'true' : 'false');
            // formData.append('syncJobId', syncJobId); // Include sync job ID for tracking
            console.log("Preparing to upload files:", files.map(f => f.name), "to directory:", directoryName.trim(), "with vision flag:", fullVision, "and job ID:", syncJobId);
            files.forEach(file => { formData.append('uploaded_files', file); });

            setUploadLoading(true);
            setUploadSuccessMessage('');
            // setJobId(syncJobId);
            setJobStartTime(null);
            setElapsedTime(0);
            // localStorage.setItem('currentJobId', syncJobId);

            // Excel Log Generation
            try {
                const logData = files.map(file => ({
                    filename: file.name, directory: directoryName.trim(), upload_time: new Date().toISOString(), size_bytes: file.size,
                    processed_on_upload: processingFlag && 'Yes' || 'No',
                    summary_prompt_id: processingFlag && selectedSummaryPromptUpload ? selectedSummaryPromptUpload.id : 'N/A',
                    extraction_prompt_id: processingFlag && selectedExtractionPromptUpload ? selectedExtractionPromptUpload.id : 'N/A',
                }));
                const wb = XLSX.utils.book_new(); const ws = XLSX.utils.json_to_sheet(logData);
                XLSX.utils.book_append_sheet(wb, ws, 'Upload Log');
                const safeDir = directoryName.trim().replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'upload';
                const logFile = `${safeDir}_log_${Date.now()}.xlsx`;
                // XLSX.writeFile(wb, logFile); // Triggers browser download
                console.log(`Generated and downloaded log: ${logFile}`);
            } catch (logErr) {
                 console.error("Excel log generation failed:", logErr);
                 setUploadErrorMessage("Could not generate Excel log (see console), continuing upload."); // Inform user but don't block
            }

            try {
                setUploadErrorMessage('Uploading files to server...');

                const response = await axios.post('/api/upload-and-save', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    withCredentials: true,
                    validateStatus: () => true // allow 207 partial
                });

                const { uploadedFiles = [], failedFiles = [], partial = false, jobId: newJobId } = response.data || {};
                const failedNames = failedFiles.map(f => f.original_filename || f.original || f.filename || '(unknown)');
                const successNames = uploadedFiles.map(f => f.original_filename || f.original || f.filename || '(unknown)');

                if ((response.status === 207 || partial) && failedNames.length > 0) {
                    // Partial success scenario
                    setPartialUpload(true);
                    setSuccessfulUploadFiles(successNames);
                    setFailedUploadFiles(failedNames);
                    setUploadSuccessMessage(response.data.message || 'Partial upload completed.');
                    const maxShow = 8;
                    const shown = failedNames.slice(0, maxShow).join(', ');
                    const more = failedNames.length > maxShow ? `, and ${failedNames.length - maxShow} more` : '';
                    setUploadErrorMessage(`Failed: ${failedNames.length} file(s): ${shown}${more}. You can retry.`);
                    // Keep only failed files in selection for retry
                    setFiles(prev => prev.filter(f => failedNames.includes(f.name)));
                    setUploadedFileNames(failedNames);
                } else if (response.status >= 200 && response.status < 300) {
                    // Full success
                    setPartialUpload(false);
                    setSuccessfulUploadFiles(successNames);
                    setFailedUploadFiles([]);
                    setUploadSuccessMessage(response.data.message || 'Files uploaded successfully!');
                    setUploadErrorMessage('');
                    // Reset form state
                    setFiles([]);
                    setDirectoryName('');
                    setUploadedFileNames([]);
                    setProcessingFlag(false);
                    setSelectedSummaryPromptUpload(null);
                    setSelectedExtractionPromptUpload(null);
                    setIsSelectingDirectory(false);
                    setFullVision(false);
                    if (document.getElementById('fileInput')) document.getElementById('fileInput').value = null;
                } else {
                    // Hard failure
                    setPartialUpload(false);
                    setSuccessfulUploadFiles([]);
                    setFailedUploadFiles(files.map(f => f.name));
                    handleApiError(response, 'uploading files');
                    const failedNamesAll = files.map(f => f.name);
                    const maxShow = 8;
                    const shown = failedNamesAll.slice(0, maxShow).join(', ');
                    const more = failedNamesAll.length > maxShow ? `, and ${failedNamesAll.length - maxShow} more` : '';
                    const baseMsg = response.data?.error || 'Upload failed.';
                    setUploadErrorMessage(`${baseMsg} Failed file(s): ${shown}${more}.`);
                    setUploadSuccessMessage('');
                }

                if (newJobId) {
                    setJobId(newJobId);
                    localStorage.setItem('currentJobId', newJobId);
                }

                // Refresh directory list and file list (if no search term)
                await fetchData('/api/directories', {}, setFetchDirsLoading, 'directories');
                if (!searchTerm.trim()) {
                    const fetchedFiles = await fetchData('/api/all_files_sorted', { limit: 50, offset: 0 }, setFetchFilesLoading, 'files after upload');
                    setAllFiles(fetchedFiles);
                }
            } catch (error) {
                handleApiError(error, 'uploading files');
                setUploadErrorMessage(error.response?.data?.error || error.message || 'Upload failed.');
                setUploadSuccessMessage('');
                setPartialUpload(false);
                setSuccessfulUploadFiles([]);
                setFailedUploadFiles(files.map(f => f.name));
                setJobId(null);
                setJobStartTime(null);
                setElapsedTime(0);
                localStorage.removeItem('currentJobId');
            } finally {
                setUploadLoading(false);
            }
        }, [files, directoryName, processingFlag, selectedSummaryPromptUpload, selectedExtractionPromptUpload, handleApiError, fetchData, searchTerm, isSelectingDirectory, fullVision]);

        // NOTE: "Process Existing" UI is currently disabled (commented out below).
        // Keep processing logic removed to avoid CI failing on no-unused-vars.

        // --- Reusable Prompt Selection UI (FileUploadHandler specific - kept same) ---
        const renderPromptsSelection = useCallback((
            selectedSummary, onSummaryChange,
            selectedExtraction, onExtractionChange,
            isLoadingPrompts, baseId
        ) => {
            const summaryId = `summaryPrompt-${baseId}`;
            const extractionId = `extractionPrompt-${baseId}`;
            return (
                <div className="processing-settings">
                    <h4>Processing Prompts</h4>
                    {isLoadingPrompts ? ( <p>Loading prompts...</p> ) : (
                        <>
                            <div className="form-section">
                                <label htmlFor={summaryId}>Summary Prompt:</label>
                                <select id={summaryId} onChange={onSummaryChange} value={selectedSummary?.id || ''} disabled={!summaryPrompts.length}>
                                    <option value="">-- Select --</option>
                                    {summaryPrompts.map(prompt => ( <option key={prompt.id} value={prompt.id}>{prompt.prompt_name}</option> ))}
                                </select>
                                {summaryPrompts.length === 0 && <small>No prompts available.</small>}
                            </div>
                            <div className="form-section">
                                <label htmlFor={extractionId}>Extraction Prompt:</label>
                                <select id={extractionId} onChange={onExtractionChange} value={selectedExtraction?.id || ''} disabled={!extractionPrompts.length}>
                                    <option value="">-- Select --</option>
                                    {extractionPrompts.map(prompt => ( <option key={prompt.id} value={prompt.id}>{prompt.prompt_name}</option> ))}
                                </select>
                                {extractionPrompts.length === 0 && <small>No prompts available.</small>}
                            </div>
                        </>
                     )}
                </div>
            );
        }, [summaryPrompts, extractionPrompts]); // Dependencies for prompt lists

        // --- JSX Return for FileUploadHandler (kept the same structure) ---
        return (
            <div className="file-upload-handler config-page-content">
                {/* Upload Section */}
                <section className="upload-section">
                    <ProtectedElement required={["upload:create"]} behaviour="disable" fallbackVariant={"overlay"}>
                    <h3>Upload New Files</h3>

                    {/* Directory Input/Select Area */}
                    <div className="form-section directory-selection">
                        <label>Directory:</label>
                        <div className="directory-mode-toggle">
                            <button
                                onClick={setDirectoryModeNew}
                                disabled={uploadLoading || fetchDirsLoading}
                                className={`toggle-button ${!isSelectingDirectory ? 'active' : ''}`}
                                type="button"
                                aria-pressed={!isSelectingDirectory}
                            >
                                Enter New
                            </button>
                            <button
                                onClick={setDirectoryModeSelect}
                                disabled={uploadLoading || fetchDirsLoading || allDirectories.length === 0}
                                className={`toggle-button ${isSelectingDirectory ? 'active' : ''}`}
                                type="button"
                                aria-pressed={isSelectingDirectory}
                                title={allDirectories.length === 0 ? "No existing directories found" : "Select Existing Directory"}
                            >
                                Select Existing
                            </button>
                        </div>

                        {isSelectingDirectory ? (
                            // Select Existing Directory Dropdown
                            <select
                                id={`directorySelect-${uploadSectionIdBase}`}
                                value={directoryName}
                                onChange={handleDirectoryInputChange}
                                disabled={uploadLoading || fetchDirsLoading || allDirectories.length === 0}
                                required // Keep validation in handler
                            >
                                <option value="">-- Select Directory --</option>
                                {fetchDirsLoading ? (
                                    <option disabled>Loading directories...</option>
                                ) : (
                                    allDirectories.map((dir) => (
                                        <option key={dir} value={dir}>{dir}</option>
                                    ))
                                )}
                            </select>
                        ) : (
                            // Enter New Directory Input
                            <input
                                id={`directoryNameUpload-${uploadSectionIdBase}`}
                                type="text"
                                placeholder="Enter new directory name"
                                value={directoryName}
                                onChange={handleDirectoryInputChange}
                                required // Keep validation in handler
                                disabled={uploadLoading}
                            />
                        )}
                        {fetchDirsLoading && isSelectingDirectory && <small> Loading directories...</small>}
                        {!fetchDirsLoading && isSelectingDirectory && allDirectories.length === 0 && <small className="warning-message"> No existing directories found.</small>}
                    </div>

                    {/* File Input */}
                    <div className="form-section">
                        <label htmlFor="fileInput">Select Files:</label>
                        <input className="file-input" id="fileInput" type="file" multiple onChange={handleFileChange} required disabled={uploadLoading} />
                        {unsupportedFileNames.length > 0 && (
                            <div className="warning-message" style={{ marginTop: 8 }}>
                                Some files are not supported and will be skipped:
                                <ul>
                                    {unsupportedFileNames.map((n, i) => (
                                        <li key={i}>{n}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                    {/* Use full Vision Parsing */}
                    <div className="fullVision-toggle">
                        <input type="checkbox" id={`fullVisionCheckbox-${uploadSectionIdBase}`} checked={fullVision} onChange={(e) => setFullVision(e.target.checked)} disabled={uploadLoading} />
                        <label htmlFor={`processingCheckbox-${uploadSectionIdBase}`}>Use vision model to parse entire document? (Preferred method for visual heavy, short documents)</label>
                    </div>
                    {/* Processing Toggle */}
                    <div className="processing-toggle">
                        <input type="checkbox" id={`processingCheckbox-${uploadSectionIdBase}`} checked={processingFlag} onChange={(e) => setProcessingFlag(e.target.checked)} disabled={uploadLoading} />
                        <label htmlFor={`processingCheckbox-${uploadSectionIdBase}`}>Process during upload?</label>
                    </div>
                    {/* Conditional Prompt Selection */}
                    {processingFlag && renderPromptsSelection(
                        selectedSummaryPromptUpload, (e) => setSelectedSummaryPromptUpload(findPromptById(prompts, e.target.value)),
                        selectedExtractionPromptUpload, (e) => setSelectedExtractionPromptUpload(findPromptById(prompts, e.target.value)),
                        promptsLoading, uploadSectionIdBase
                    )}
                    {/* Upload Button */}
                    <div className="button-group">
                        <button
                            onClick={handleFileUpload}
                            disabled={uploadLoading || files.length === 0 || !directoryName.trim()}
                            className="primary"
                        >
                            {uploadLoading ? 'Uploading...' : 'Upload Files'}
                        </button>
                        {uploadErrorMessage && uploadErrorMessage.startsWith('Failed:') && files.length > 0 && (
                            <button
                                onClick={handleFileUpload}
                                disabled={uploadLoading || files.length === 0 || !directoryName.trim()}
                                className="secondary"
                                title="Retry failed files"
                            >
                                Retry Failed Files
                            </button>
                        )}
                    </div>
                    {/* Job Progress Display */}
                    {!jobProgress && jobId && (
                        <div className="job-progress-message" style={{
                            border: '1px solid var(--border-color, #e3e3e3)',
                            borderRadius: 10,
                            padding: 12,
                            background: 'var(--panel-bg, #0b0f14)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <strong>Job Progress</strong>
                                    <span className="chip" style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
                                        starting
                                    </span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <svg width="18" height="18" viewBox="0 0 50 50" aria-label="loading">
                                    <circle cx="25" cy="25" r="20" stroke="#9aa1ac" strokeWidth="5" fill="none" strokeLinecap="round">
                                        <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                                    </circle>
                                </svg>
                                <span style={{ color: 'rgba(255,255,255,0.85)' }}>Connecting to job tracker...</span>
                            </div>
                        </div>
                    )}
                    {jobProgress && (() => {
                        const processed = Number(jobProgress.processedFiles ?? jobProgress.completedFiles ?? 0);
                        const extracted = Number(jobProgress.extractedFiles ?? 0);
                        const total = Number(jobProgress.totalFiles ?? Math.max(processed, extracted, 0));
                        const extractionPlannedRaw = String(jobProgress.extractionPlanned || '').toLowerCase();
                        const extractionPlanned = extractionPlannedRaw === 'true' || extractionPlannedRaw === '1';
                        const processedFrac = total > 0 ? Math.min(1, processed / total) : 0;
                        const extractedFrac = total > 0 ? Math.min(1, extracted / total) : 0;
                        // Weighting: 30% processing + 70% extraction when extraction is planned; else processing drives 100%
                        const weighted = extractionPlanned ? (processedFrac * 30) + (extractedFrac * 70) : (processedFrac * 100);
                        const pct = Math.min(100, Math.round(weighted));
                        const isLoading = ["pending", "running", "queued"].includes(String(jobProgress.status || '').toLowerCase());
                        const phase = jobProgress.phase || (isLoading ? 'processing' : 'idle');

                        return (
                            <div className="job-progress-message" style={{
                                border: '1px solid var(--border-color, #e3e3e3)',
                                borderRadius: 10,
                                padding: 12,
                                background: 'var(--panel-bg, #0b0f14)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span role="img" aria-label="progress">📦</span>
                                        <strong>Job Progress</strong>
                                        {phase && <span className="chip" style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>{phase}</span>}
                                    </div>
                                    {jobProgress.status && (
                                        <span className={`status-badge status-${String(jobProgress.status).toLowerCase()}`}>
                                            {jobProgress.status}
                                        </span>
                                    )}
                                </div>

                                {/* Progress Bar */}
                                <div style={{ margin: '6px 0 10px' }}>
                                    <div style={{
                                        position: 'relative',
                                        width: '100%',
                                        height: 10,
                                        background: 'rgba(255,255,255,0.08)',
                                        borderRadius: 999,
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            width: `${pct}%`,
                                            height: '100%',
                                            background: 'linear-gradient(90deg, #4cc9f0, #4361ee)',
                                            transition: 'width 300ms ease',
                                        }} />
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 6, gap: 8, flexWrap: 'wrap' }}>
                                        <span>Extracted: <strong>{extracted}</strong> / <strong>{total}</strong> ({pct}%)</span>
                                        <span>Processed: <strong>{processed}</strong> / <strong>{total}</strong></span>
                                        {(() => {
                                            const pf = Number(jobProgress.processedFailedFilesCount || 0);
                                            const ef = Number(jobProgress.extractedFailedFilesCount || 0);
                                            if (pf > 0 || ef > 0) {
                                                return (
                                                    <span style={{ color: 'var(--danger, #d00000)' }}>
                                                        Failures: {pf} processing{ef ? `, ${ef} extraction` : ''}
                                                    </span>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>
                                </div>

                                {/* Loading Indicator + Cost/Time */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                    {isLoading && (
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                            {/* Lightweight inline spinner (SVG) */}
                                            <svg width="18" height="18" viewBox="0 0 50 50" aria-label="loading">
                                                <circle cx="25" cy="25" r="20" stroke="#9aa1ac" strokeWidth="5" fill="none" strokeLinecap="round">
                                                    <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                                                </circle>
                                            </svg>
                                            <span style={{ color: 'rgba(255,255,255,0.85)' }}>Working...</span>
                                        </div>
                                    )}

                                    {jobProgress.totalCost !== undefined && !isNaN(parseFloat(jobProgress.totalCost)) && (
                                        <div style={{ color: '#2b9348', fontWeight: 500, fontSize: '1.05em' }}>
                                            <span role="img" aria-label="cost">💲</span>
                                            &nbsp;Total Cost: <span style={{ fontFamily: 'monospace' }}>${parseFloat(jobProgress.totalCost).toFixed(4)}</span>
                                        </div>
                                    )}

                                    {jobStartTime && (
                                        <div style={{ color: '#fca311', fontWeight: 500, fontSize: '0.95em' }}>
                                            <span role="img" aria-label="started">⏱️</span>
                                            &nbsp;Time: <span style={{ fontFamily: 'monospace' }}>{formatElapsedTime(elapsedTime)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                    {/* Extraction failure details, if any */}
                    {jobProgress && (() => {
                        const raw = jobProgress.extractedFailedFiles;
                        let items = [];
                        if (Array.isArray(raw)) items = raw;
                        else if (typeof raw === 'string') {
                            try { items = JSON.parse(raw); } catch (e) { items = []; }
                        }
                        if (!items || items.length === 0) return null;
                        return (
                            <div style={{ marginTop: 8, border: '1px solid rgba(220,20,60,0.3)', borderRadius: 8, padding: 10, background: 'rgba(220,20,60,0.08)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                    <span role="img" aria-label="extraction-failed">⚠️</span>
                                    <strong>Extraction Failures</strong>
                                    <span style={{ color: 'var(--danger, #d00000)' }}>({items.length})</span>
                                </div>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {items.map((n, i) => (
                                        <li key={i} style={{ color: 'var(--danger, #d00000)' }}>{String(n)}</li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })()}
                    {/* Processing failure details, if any */}
                    {jobProgress && (() => {
                        const raw = jobProgress.processedFailedFiles;
                        let items = [];
                        if (Array.isArray(raw)) items = raw;
                        else if (typeof raw === 'string') {
                            try { items = JSON.parse(raw); } catch (e) { items = []; }
                        }
                        if (!items || items.length === 0) return null;
                        return (
                            <div style={{ marginTop: 8, border: '1px solid rgba(220,20,60,0.3)', borderRadius: 8, padding: 10, background: 'rgba(220,20,60,0.08)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                    <span role="img" aria-label="processing-failed">⚠️</span>
                                    <strong>Processing Failures</strong>
                                    <span style={{ color: 'var(--danger, #d00000)' }}>({items.length})</span>
                                </div>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {items.map((n, i) => (
                                        <li key={i} style={{ color: 'var(--danger, #d00000)' }}>{String(n)}</li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })()}
                    {/* Status Messages */}
                    {uploadLoading && (
                        <div className="loading-message">
                            {uploadErrorMessage.includes('Uploading') ? uploadErrorMessage : 'Uploading files...'}
                        </div>
                    )}
                    {uploadSuccessMessage && <div className="success-message">{uploadSuccessMessage}</div>}
                    {uploadErrorMessage && <div className="error-message">{uploadErrorMessage}</div>}
                    {/* Partial Upload Results */}
                    {partialUpload && (
                        <div className="partial-upload-results" style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginTop: 12 }}>
                            <div style={{ flex: '1 1 240px' }}>
                                <h4 style={{ margin: '4px 0' }}>Succeeded ({successfulUploadFiles.length}):</h4>
                                {successfulUploadFiles.length === 0 ? <small>None</small> : (
                                    <ul>{successfulUploadFiles.map((n, i) => <li key={i} style={{ color: 'var(--success, #2b9348)' }}>{n}</li>)}</ul>
                                )}
                            </div>
                            <div style={{ flex: '1 1 240px' }}>
                                <h4 style={{ margin: '4px 0' }}>Failed ({failedUploadFiles.length}):</h4>
                                {failedUploadFiles.length === 0 ? <small>None</small> : (
                                    <ul>{failedUploadFiles.map((n, i) => <li key={i} style={{ color: 'var(--danger, #d00000)' }}>{n}</li>)}</ul>
                                )}
                            </div>
                        </div>
                    )}
                    {/* Pre-Upload Selected Files (only if not partial) */}
                    {!partialUpload && uploadedFileNames.length > 0 && !uploadLoading && (
                        <div className="uploaded-files-log" style={{ marginTop: 12 }}>
                            <h4>Selected ({uploadedFileNames.length}):</h4>
                            <ul> {uploadedFileNames.map((name, index) => ( <li key={index}>{name}</li> ))} </ul>
                        </div>
                    )}
                    </ProtectedElement>
                </section>
                {/* Process Existing Section */}
                {/* <section className="process-section">
                    <h3>Process Existing Files</h3>
                    {/* Search 
                    <div className="search-container form-section">
                        <input type="text" placeholder="Search files..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} disabled={fetchFilesLoading || processLoading} aria-label="Search existing files" />
                        <button onClick={handleSearch} disabled={fetchFilesLoading || processLoading || !searchTerm.trim()} className="secondary">Search</button>
                        <button onClick={() => { setSearchTerm(''); handleSearch(); }} disabled={fetchFilesLoading || processLoading} className="cancel" title="Clear Search & Reload All Files">Clear</button>
                    </div>
                    {fetchFilesLoading && <div className="loading-message compact">Searching files...</div>}

                    {/* File Selection List 
                    <div className="selection-area">
                         <label>Select Files to Process:</label>
                         <button onClick={handleSelectAllToggle} className="select-all-button" disabled={allFiles.length === 0 || processLoading || fetchFilesLoading}>
                             {selectedFilesForProcessing.length === allFiles.length && allFiles.length > 0 ? 'Deselect All' : 'Select All'} ({selectedFilesForProcessing.length} / {allFiles.length})
                         </button>
                        <div className="file-list-container">
                             {allFiles.length > 0 ? (
                                // Sort files alphabetically by name for display
                                allFiles.slice().sort((a, b) => (a.file_name || '').localeCompare(b.file_name || '')).map(file => {
                                    const fileItemId = `file-checkbox-${processSectionIdBase}-${file.id}`;
                                    return ( <div key={file.id} className="file-list-item">
                                        <input type="checkbox" id={fileItemId} checked={selectedFilesForProcessing.some(sf => sf.id === file.id)} onChange={() => handleFileCheckboxChange(file)} disabled={processLoading || fetchFilesLoading}/>
                                        <label htmlFor={fileItemId}>{file.file_name} {file.directory ? `(${file.directory})` : ''}</label>
                                    </div> );
                                })
                             ) : ( !fetchFilesLoading && <div className="empty-list-message">No files found{searchTerm ? ` matching "${searchTerm}"` : ""}. Use search or check connection.</div> )}
                        </div>
                    </div>

                    {/* Conditional Prompt Selection for Processing 
                    {selectedFilesForProcessing.length > 0 && renderPromptsSelection(
                        selectedSummaryPromptProcess, (e) => setSelectedSummaryPromptProcess(findPromptById(prompts, e.target.value)),
                        selectedExtractionPromptProcess, (e) => setSelectedExtractionPromptProcess(findPromptById(prompts, e.target.value)),
                        promptsLoading, processSectionIdBase
                    )}

                    {/* Process Button
                    <div className="button-group">
                        <button onClick={handleStartProcessing} disabled={processLoading || fetchFilesLoading || selectedFilesForProcessing.length === 0 || !selectedSummaryPromptProcess || !selectedExtractionPromptProcess} className="primary">
                            {processLoading ? 'Starting Processing...' : `Process ${selectedFilesForProcessing.length} Selected File(s)`}
                        </button>
                    </div>
                    {/* Status Messages for Processing *}
                     {processLoading && <div className="loading-message">Submitting processing request...</div>}
                    {processSuccessMessage && <div className="success-message">{processSuccessMessage}</div>}
                    {processErrorMessage && <div className="error-message">{processErrorMessage}</div>}
                </section> */}
            </div>
        );
    }), []); // End of FileUploadHandler


    // --- Sub-Components for Content Areas (Memoized - most kept same) ---

    // --- UPDATED: MainContent passes isSaving down ---
    const MainContent = React.memo(({ settings, providerModelMap, maxTokenValue, handleSettingChange, handleSliderChange, handleSaveSettings, isSaving }) => {
        // Calculate percentages for sliders (visual feedback)
        const tempPercent = ((settings.temperature || 0) / 2) * 100; // Assuming max temp is 2
        // Adjust token percentage calculation to avoid division by zero if maxTokenValue is 1
        const tokenPercent = maxTokenValue > 1 ? (((settings.maxTokens || 1) - 1) / (maxTokenValue - 1)) * 100 : 0;
        // Get available models for the selected provider
        const availableModels = settings.llmProvider ? providerModelMap[settings.llmProvider] || [] : [];

        return (
            <div className="main-settings-content config-page-content">
                <ProtectedElement required={["user:update-settings"]} behavior="disabled" fallbackVariant={"overlay"}>
                <h3>Main LLM Settings</h3>
                <p>Configure your main Large Language Model (LLM) settings. Changes are saved to your user profile on the server.</p>
                <div className="settings-grid">
                    {/* Provider Selection */}
                    <div className="settings-label-cell"><label htmlFor="llmProvider">Provider:</label></div>
                    <div className="settings-control-cell">
                        <select id="llmProvider" value={settings.llmProvider} onChange={(e) => handleSettingChange('llmProvider', e.target.value)} disabled={isSaving} className='main-select'>
                            {Object.keys(providerModelMap).map(provider => (
                                <option key={provider} value={provider}>{provider}</option>
                            ))}
                        </select>
                    </div>
                    {/* Model Selection */}
                    <div className="settings-label-cell"><label htmlFor="llmModel">Model:</label></div>
                    <div className="settings-control-cell">
                        <select id="llmModel" className='main-select' value={settings.model} onChange={(e) => handleSettingChange('model', e.target.value)} disabled={isSaving || !settings.llmProvider || availableModels.length === 0}>
                            {!settings.llmProvider ? ( <option value="">Select provider first</option> ) :
                             availableModels.length > 0 ? (
                                availableModels.map(model => ( <option key={model.name} value={model.name}>{model.name}</option> ))
                             ) : ( <option value="">No models listed for provider</option> )}
                        </select>
                    </div>
                    {/* Endpoint URL Display (Read-only) */}
                    <div className="settings-label-cell"><label htmlFor="modelUrl">Endpoint URL:</label></div>
                    <div className="settings-control-cell">
                         <input id="modelUrl" className='main-select' type="text" value={settings.url || 'N/A'} readOnly title={settings.url || 'N/A'} disabled={isSaving}/>
                    </div>
                    {/* Temperature Slider */}
                    <div className="settings-label-cell"><label htmlFor="temperature">Temperature:</label></div>
                    <div className="settings-control-cell slider-group">
                        <input id="temperature" type="range" min="0" max="2" step="0.05" value={settings.temperature} onChange={(e) => handleSliderChange('temperature', e.target.value)} style={{ '--value-percent': `${tempPercent}%` }} disabled={isSaving} />
                        <span>{settings.temperature?.toFixed(2) ?? 'N/A'}</span>
                    </div>
                    {/* Max Tokens Slider */}
                    <div className="settings-label-cell"><label htmlFor="maxTokens">Max Tokens:</label></div>
                    <div className="settings-control-cell slider-group">
                        <input id="maxTokens" type="range" min="1" max={maxTokenValue || 1} step={maxTokenValue > 1000 ? 50 : 1} value={settings.maxTokens} onChange={(e) => handleSliderChange('maxTokens', e.target.value)} disabled={isSaving || !maxTokenValue || maxTokenValue <= 1} style={{ '--value-percent': `${tokenPercent}%` }} />
                        <span>{settings.maxTokens ?? 'N/A'}</span>
                        <small>(Model Max: {maxTokenValue ? maxTokenValue.toLocaleString() : 'N/A'})</small>
                    </div>
                    {/* Save Button */}
                    <div className="modal-button-group" style={{ gridColumn: '1 / -1' }}>
                        <button onClick={handleSaveSettings} className="primary" disabled={isSaving}>
                                {isSaving ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </div>
                </ProtectedElement>
                {/* Optional: Display saving indicator */}
                {/* {isSaving && <div className="loading-message compact" style={{marginTop: '10px'}}>Saving...</div>} */}
            </div>
        );
    });    


    const PromptManagementContent = React.memo(({
        prompts,
        selectedPrompt,
        handleSelectPromptChange,
        handleUpdatePrompt,
        handleCreatePrompt,
        promptsLoading,
        clearSelectedPrompt,
        globalSettings,
        providerModelMap,
        handleDeletePrompt
    }) => {
        // --- START: Tool constants moved here for encapsulation ---
        const DEFAULT_TOOLS_STATE = useMemo(() => ({
            // All tools off by default
            relevantText: false,
            googlesearch: false,
            queryExternalDatabase: false,
            exploreKnowledgeGraph: false,
            searchCollectionKnowledge: false,
            saveTextToFile: false,
            listFilesInDirectory: false,
            readFileContent: false,
            analyticsEngine: false,
            daxCoder: false,
            Knowledge_query: false,
            listAllMyFiles: false,
            image_annotation: false,
            generate_image: false,
            createDirectory: false,
            scrapeWebsite: false,
            outlookAssistant: false,
        }), []);

        const FILE_TOOLS_GROUP_KEYS = useMemo(
            () => ['saveTextToFile', 'readFileContent', 'listAllMyFiles', 'listFilesInDirectory', 'createDirectory'],
            []
        );

        const SEARCH_SCRAPE_GROUP_KEYS = useMemo(
            () => ['googleSearch', 'scrapeWebsite', 'getStockInformation'],
            []
        );

        const TOOL_GROUPS = {
            retrieval: ['relevantText', 'exploreKnowledgeGraph', 'queryExternalDatabase', 'queryKnowledgeBase','listDocumentSections','retrieveDocumentSections'],
            'search & scrape': ['googleSearch', 'scrapeWebsite', 'getStockInformation'], // NEW GROUP
            file: ['FileTools'],
            media: ['image_annotation', 'generate_image'],
            analysis: ['analyticsEngine', 'daxCoder'],
            integrations: ['outlookAssistant'],
        };

        const TOOL_DESCRIPTIONS = {
            FileTools: "A single option to enable all core file system operations: save, list, read, create directories, and list all personal files.", // New description for the meta-tool
            SearchScrapeTools: "A single option to enable all search and web scraping tools.", // New description for the meta-tool
            relevantText: "Return relevant text from articles in a collection based on user input.",
            searchCollectionKnowledge: "Search a knowledge graph collection for facts/statements using keywords.",
            collection_knowledge: "List graph facts/statements from a selected collection and use these to answer a question.",
            exploreKnowledgeGraph: "Explore a knowledge graph collection and synthesize an answer.",
            googleSearch: "Search Google for given keywords.",
            queryExternalDatabase: "Query a database for information based on your question.",
            saveTextToFile: "Save provided text to a file in the File Explorer.",
            listFilesInDirectory: "List files in a specified directory in the File Explorer.",
            readFileContent: "Read and return the content of a file in the File Explorer.",
            analyticsEngine: "Perform calculations or data analysis using Python code.",
            daxCoder: "Generate DAX formulas for Power BI based on user queries and provided data context.",
            outlookAssistant: "Runs an Outlook specialist sub-agent to list/search emails, read a message by id, or create a draft email.",
            queryKnowledgeBase: "Query the Pearl Database for documents and information.",
            listDocumentSections: "List sections of a document in the Main database based on a document ID.",
            retrieveDocumentSections: "Retrieve specific sections of a document in the Main database based on a document ID and section IDs.",
            listAllMyFiles: "Provides a complete list of all file and directory paths within your personal Home workspace.",
            image_annotation: "Analyzes an image based on a query. Requires 'image_file_path' (relative to user's Home, e.g., 'Pictures/my_image.jpg') and 'query' (text prompt for the image). ex. {'image_file_path': 'Images/cat.jpg', 'query': 'What color is the cat?'}",
            generate_image: "Generates an image based on a detailed textual prompt using DALL-E. Saves the image to the user's Home/Images directory with a specified filename.",
            createDirectory: "Use to create a new directory inside the user's Home workspace. This can also create nested directories. Provide the name for the new directory. Example: {'directory_name': 'MyNewProject'}",
            scrapeWebsite: "Use to scrape the text content from a given web URL. Expects a 'url' parameter. ex. {'url': 'https://en.wikipedia.org/wiki/Python_(programming_language)'}",
            getStockInformation: "Fetches historical stock market data for a list of one or more stock tickers from a specified start date. ex. {'tickers': ['AAPL', 'MSFT', 'NVDA'], 'start_date': '2025-07-07'}",
        };
        // --- END: Tool constants ---

        const [internalPromptName, setInternalPromptName] = useState('');
        const [internalPromptText, setInternalPromptText] = useState('');
        const [internalPromptGroup, setInternalPromptGroup] = useState('');
    
        const [useCustomPromptSettings, setUseCustomPromptSettings] = useState(false);
        const [promptProvider, setPromptProvider] = useState(globalSettings.llmProvider || 'OpenAI');
        const [promptModel, setPromptModel] = useState(globalSettings.model || '');
        const [promptTemperature, setPromptTemperature] = useState(globalSettings.temperature || 0.5);
        const [promptMaxTokens, setPromptMaxTokens] = useState(globalSettings.maxTokens || 1000);
        const [promptModelMaxLimit, setPromptModelMaxLimit] = useState(16000);

        const [useCustomPromptTools, setUseCustomPromptTools] = useState(false);
        const [promptTools, setPromptTools] = useState(DEFAULT_TOOLS_STATE);
        //  independent toggle for allowing agent tool discovery
        const [toolDiscovery, setToolDiscovery] = useState(false);
        // collections management state
        const [availableCollections, setAvailableCollections] = useState([]); // [{id, name}] when wired up
        const [selectedCollectionId, setSelectedCollectionId] = useState('');
        const [manualCollectionId, setManualCollectionId] = useState('');
        const [collectionIds, setCollectionIds] = useState([]);

        // Fetch available collections (ids and names) on mount
        useEffect(() => {
            let isMounted = true;
            (async () => {
                try {
                    const resp = await fetch(`${BASE_URL}/api/files/get_collection_ids_and_names`, { credentials: 'include' });
                    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                    const data = await resp.json();
                    const arr = Array.isArray(data) ? data : (Array.isArray(data?.collections) ? data.collections : []);
                    const normalized = arr
                        .map(item => {
                            if (typeof item === 'string') return { id: item, name: item };
                            if (item && typeof item === 'object') {
                                const id = item.id ?? item.collection_id ?? item.collectionId ?? item.value ?? item.key;
                                const name = item.name ?? item.collection_name ?? item.label ?? String(id ?? '');
                                if (id == null || id === '') return null;
                                return { id: String(id), name: String(name) };
                            }
                            return null;
                        })
                        .filter(Boolean);
                    if (isMounted) setAvailableCollections(normalized);
                } catch (err) {
                    if (isMounted) setAvailableCollections([]);
                    console.info('Collections fetch skipped/failed:', err?.message || err);
                }
            })();
            return () => { isMounted = false; };
        }, []);
    
        useEffect(() => {
            const currentProviderModels = providerModelMap[promptProvider] || [];
            const currentModelDetails = currentProviderModels.find(m => m.name === promptModel);
    
            if (currentModelDetails) {
                const modelMax = parseInt(currentModelDetails.model_max_tokens, 10);
                setPromptModelMaxLimit(isNaN(modelMax) || modelMax <= 0 ? 16000 : modelMax);
                setPromptMaxTokens(prev => Math.min(prev, isNaN(modelMax) || modelMax <= 0 ? 16000 : modelMax));
            } else if (currentProviderModels.length > 0) {
                if (!currentProviderModels.find(m => m.name === promptModel)) {
                    setPromptModel(currentProviderModels[0].name);
                    const firstModelDetails = currentProviderModels[0];
                    const modelMax = parseInt(firstModelDetails.model_max_tokens, 10);
                    setPromptModelMaxLimit(isNaN(modelMax) || modelMax <= 0 ? 16000 : modelMax);
                    setPromptMaxTokens(prev => Math.min(prev, isNaN(modelMax) || modelMax <= 0 ? 16000 : modelMax));
                }
            } else {
                setPromptModel('');
                setPromptModelMaxLimit(16000);
            }
        }, [promptProvider, promptModel, providerModelMap]);
    
        useEffect(() => {
            if (selectedPrompt) {
                setInternalPromptName(selectedPrompt.prompt_name || '');
                setInternalPromptText(selectedPrompt.prompt_text || '');
                setInternalPromptGroup(selectedPrompt.group || ''); 
    
                if (selectedPrompt.provider && selectedPrompt.model) {
                    setUseCustomPromptSettings(true);
                    setPromptProvider(selectedPrompt.provider);
                    setPromptModel(selectedPrompt.model);
                    setPromptTemperature(parseFloat(selectedPrompt.temperature) || globalSettings.temperature);
                    setPromptMaxTokens(parseInt(selectedPrompt.max_tokens, 10) || globalSettings.maxTokens);
                    const modelDetails = getModelDetails(selectedPrompt.provider, selectedPrompt.model, providerModelMap);
                    if (modelDetails) {
                        const modelMax = parseInt(modelDetails.model_max_tokens, 10);
                        setPromptModelMaxLimit(isNaN(modelMax) || modelMax <= 0 ? 16000 : modelMax);
                    }
                } else { 
                    setUseCustomPromptSettings(false);
                    // Reset to global defaults when a prompt without custom settings is loaded
                    setPromptProvider(globalSettings.llmProvider || 'OpenAI');
                    setPromptModel(globalSettings.model || '');
                    setPromptTemperature(globalSettings.temperature || 0.5);
                    setPromptMaxTokens(globalSettings.maxTokens || 1000);
                }

                if (selectedPrompt.agent_tools && typeof selectedPrompt.agent_tools === 'object') {
                    setUseCustomPromptTools(true);
                    // Merge saved tools with defaults to ensure all keys are present
                    setPromptTools({ ...DEFAULT_TOOLS_STATE, ...selectedPrompt.agent_tools });
                    // if FileTools group is enabled, ensure all individual file tools are also enabled
                    if (selectedPrompt.agent_tools.FileTools) {
                        setPromptTools(prev => {
                            const updated = { ...prev };
                            FILE_TOOLS_GROUP_KEYS.forEach(key => {
                                updated[key] = true;
                            });
                            return updated;
                        });
                    }
                    // if SearchScrapeTools group is enabled, ensure all individual search & scrape tools are also enabled
                    if (selectedPrompt.agent_tools.SearchScrapeTools) {
                        setPromptTools(prev => {
                            const updated = { ...prev };
                            SEARCH_SCRAPE_GROUP_KEYS.forEach(key => {
                                updated[key] = true;
                            });
                            return updated;
                        });
                    }

                } else {
                    setUseCustomPromptTools(false);
                    setPromptTools(DEFAULT_TOOLS_STATE);
                }
                // --- END: Logic for populating tools ---

                // Tool discovery (independent of custom tools)
                if (Object.prototype.hasOwnProperty.call(selectedPrompt, 'tool_discovery')) {
                    setToolDiscovery(!!selectedPrompt.tool_discovery);
                } else {
                    setToolDiscovery(false);
                }

                // Initialize collections list if present on the selected prompt (non-blocking if absent)
                if (Array.isArray(selectedPrompt.collection_ids)) {
                    setCollectionIds(selectedPrompt.collection_ids.map(String));
                } else if (Array.isArray(selectedPrompt.agent_collection_ids)) {
                    setCollectionIds(selectedPrompt.agent_collection_ids.map(String));
                } else {
                    setCollectionIds([]);
                }

                // Fetch latest collection ids from backend for this prompt id
                if (selectedPrompt.id) {
                    (async () => {
                        try {
                            const resp = await fetch(`${BASE_URL}/api/prompts/${selectedPrompt.id}/collections`, { credentials: 'include' });
                            if (resp.ok) {
                                const data = await resp.json();
                                const ids = Array.isArray(data?.collection_ids) ? data.collection_ids.map(String) : [];
                                setCollectionIds(ids);
                            }
                        } catch (e) {
                            // non-blocking: leave whatever we had
                            console.info('Failed to fetch prompt collections; using existing values');
                        }
                    })();
                }

            } else { 
                // Reset all fields for a new prompt
                setInternalPromptName('');
                setInternalPromptText('');
                setInternalPromptGroup('');
                setUseCustomPromptSettings(false);
                setUseCustomPromptTools(false); // Also reset this
                setPromptTools(DEFAULT_TOOLS_STATE); // And this
                setToolDiscovery(false); // reset tool discovery
                setCollectionIds([]); // reset collection ids
                // Reset LLM settings to global
                setPromptProvider(globalSettings.llmProvider || 'OpenAI');
                const initialProvider = globalSettings.llmProvider || 'OpenAI';
                const initialModels = providerModelMap[initialProvider] || [];
                setPromptModel(globalSettings.model || (initialModels.length > 0 ? initialModels[0].name : ''));
                setPromptTemperature(globalSettings.temperature || 0.5); 
                setPromptMaxTokens(globalSettings.maxTokens || 1000); 
            }
        }, [
            selectedPrompt,
            globalSettings,
            providerModelMap,
            DEFAULT_TOOLS_STATE,
            FILE_TOOLS_GROUP_KEYS,
            SEARCH_SCRAPE_GROUP_KEYS,
        ]);
    
        const handlePromptLLMSettingChange = (key, value) => {
            if (key === 'provider') {
                setPromptProvider(value);
                const firstModel = providerModelMap[value]?.[0];
                if(firstModel) setPromptModel(firstModel.name);
            }
            if (key === 'model') setPromptModel(value);
            if (key === 'temperature') setPromptTemperature(parseFloat(value));
            if (key === 'maxTokens') setPromptMaxTokens(parseInt(value, 10));
        };
        
        const handlePromptToolChange = (toolKey) => {
            // Handle meta-tool toggles
            if (toolKey === 'FileTools') {
                const newValue = !promptTools.FileTools;
                setPromptTools(prev => {
                    const updated = { ...prev, FileTools: newValue };
                    FILE_TOOLS_GROUP_KEYS.forEach(key => {
                        updated[key] = newValue;
                    });
                    return updated;
                });
            } else if (toolKey === 'SearchScrapeTools') {
                const newValue = !promptTools.SearchScrapeTools;
                setPromptTools(prev => {
                    const updated = { ...prev, SearchScrapeTools: newValue };
                    SEARCH_SCRAPE_GROUP_KEYS.forEach(key => {
                        updated[key] = newValue;
                    });
                    return updated;
                });
            } else {
                // Any other individual tool
                setPromptTools(prev => {
                    const updated = { ...prev, [toolKey]: !prev[toolKey] };
                    return updated;
                });
            }
        };

        const onClearSelectedPrompt = () => {
            clearSelectedPrompt(); 
        };
    
        const prepareLLMSettingsPayload = () => {
            if (!useCustomPromptSettings) return { provider: null, model: null, temperature: null, max_tokens: null };
            if (!promptProvider || !promptModel) {
                alert("If using custom LLM settings, Provider and Model must be selected.");
                return null; 
            }
            return { provider: promptProvider, model: promptModel, temperature: promptTemperature, max_tokens: promptMaxTokens };
        };

        // --- START: New payload preparer for tools ---
        const prepareToolsPayload = () => {
            // If the custom tools checkbox is not checked, send null to the backend.
            if (!useCustomPromptTools) {
                return { tools: null, tool_discovery: toolDiscovery };
            }
            // Otherwise, send the current state of the tools checkboxes.
            return { tools: promptTools, tool_discovery: toolDiscovery };
        };
        // --- END: New payload preparer for tools ---

        // Prep function for future collections association API (not invoked yet)
        const prepareCollectionsPayload = () => {
            return { collection_ids: collectionIds };
        };

        // Handlers for managing collection IDs locally
        const handleAddSelectedCollection = () => {
            if (!selectedCollectionId) return;
            setCollectionIds(prev => (prev.includes(selectedCollectionId) ? prev : [...prev, selectedCollectionId]));
        };

        const handleAddManualCollection = () => {
            const id = (manualCollectionId || '').trim();
            if (!id) return;
            setCollectionIds(prev => (prev.includes(id) ? prev : [...prev, id]));
            setManualCollectionId('');
        };

        const handleRemoveCollection = (id) => {
            setCollectionIds(prev => prev.filter(cid => cid !== id));
        };
    
        const canSubmit = internalPromptName.trim() && internalPromptText.trim() && (!useCustomPromptSettings || (promptProvider && promptModel));
    
        const availablePromptModels = promptProvider ? providerModelMap[promptProvider] || [] : [];
        const promptTempPercent = ((promptTemperature || 0) / 2) * 100;
        const promptTokenPercent = promptModelMaxLimit > 1 ? (((promptMaxTokens || 1) - 1) / (promptModelMaxLimit - 1)) * 100 : 0;

        const isSystemPrompt = selectedPrompt && (selectedPrompt.username === null || selectedPrompt.username === '');
    
        return (
            <div className="prompts-content-area config-page-content">
                <ProtectedElement required={["prompts:read"]} behavior="disabled" fallbackVariant={"overlay"}>
                <h3>Manage Prompts</h3>
                <p>View, edit, or create new prompts. Optionally, assign specific LLM settings and Agent Tools to a prompt.</p>
                {promptsLoading && <div className="loading-message compact">Loading prompts...</div>}
                <div className="form-section">
                    <label htmlFor="promptSelect">Select Prompt to View/Edit:</label>
                    <select id="promptSelect" onChange={handleSelectPromptChange} value={selectedPrompt?.id || ''} disabled={promptsLoading || prompts.length === 0}>
                        <option value="">-- Select --</option>
                        {prompts.slice().sort((a, b) => (a.prompt_name || '').localeCompare(b.prompt_name || '')).map(prompt => (
                            <option key={prompt.id} value={prompt.id}>{prompt.prompt_name} {prompt.username === null || prompt.username === '' ? '(System)' : `(${prompt.username})`}</option>
                        ))}
                    </select>
                </div>
    
                <div className="prompt-details-section">
                    <h4>{selectedPrompt ? (isSystemPrompt ? `View System Prompt: ${selectedPrompt.prompt_name}` : `Edit Prompt: ${selectedPrompt.prompt_name}`) : 'Create New Prompt'}</h4>
                    <div className="form-section">
                        <label htmlFor="promptName">Name:</label>
                        <input id="promptName" type="text" placeholder="Enter prompt name" value={internalPromptName} onChange={(e) => setInternalPromptName(e.target.value)} />
                    </div>
                    <div className="form-section">
                        <label htmlFor="promptText">Text:</label>
                        <textarea id="promptText" placeholder="Enter prompt text" value={internalPromptText} onChange={(e) => setInternalPromptText(e.target.value)} rows="8" />
                    </div>
                    <div className="form-section">
                        <label htmlFor="promptGroup">Group (optional, e.g., summary, extraction):</label>
                        <input id="promptGroup" type="text" placeholder="Enter group name" value={internalPromptGroup} onChange={(e) => setInternalPromptGroup(e.target.value)} />
                    </div>
    
                    <div className="form-section prompt-llm-settings-section" style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px dashed #ccc' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                            <input type="checkbox" id="useCustomPromptSettings" checked={useCustomPromptSettings} onChange={(e) => setUseCustomPromptSettings(e.target.checked)} style={{ marginRight: '10px', width: 'auto', height: 'auto' }} />
                            <label htmlFor="useCustomPromptSettings" style={{ marginBottom: '0', fontWeight: 'normal' }}>Use custom LLM settings for this prompt</label>
                        </div>
                        {useCustomPromptSettings && (
                            <div className="settings-grid" style={{ opacity: useCustomPromptSettings ? 1 : 0.5, pointerEvents: useCustomPromptSettings ? 'auto' : 'none' }}>
                                {/* LLM Settings Grid */}
                                <div className="settings-label-cell"><label htmlFor="promptProvider">Provider:</label></div>
                                <div className="settings-control-cell">
                                    <select id="promptProvider" value={promptProvider} onChange={(e) => handlePromptLLMSettingChange('provider', e.target.value)}>
                                        {Object.keys(providerModelMap).map(pKey => (<option key={pKey} value={pKey}>{pKey}</option>))}
                                    </select>
                                </div>
                                <div className="settings-label-cell"><label htmlFor="promptModel">Model:</label></div>
                                <div className="settings-control-cell">
                                    <select id="promptModel" value={promptModel} onChange={(e) => handlePromptLLMSettingChange('model', e.target.value)} disabled={!promptProvider || availablePromptModels.length === 0}>
                                        {!promptProvider ? ( <option value="">Select provider</option> ) :
                                        availablePromptModels.length > 0 ? (
                                            availablePromptModels.map(model => ( <option key={model.name} value={model.name}>{model.name}</option> ))
                                        ) : ( <option value="">No models</option> )}
                                    </select>
                                </div>
                                <div className="settings-label-cell"><label htmlFor="promptTemperature">Temperature:</label></div>
                                <div className="settings-control-cell slider-group">
                                    <input id="promptTemperature" type="range" min="0" max="2" step="0.05" value={promptTemperature} onChange={(e) => handlePromptLLMSettingChange('temperature', e.target.value)} style={{ '--value-percent': `${promptTempPercent}%` }}/>
                                    <span>{promptTemperature?.toFixed(2) ?? 'N/A'}</span>
                                </div>
                                <div className="settings-label-cell"><label htmlFor="promptMaxTokens">Max Tokens:</label></div>
                                <div className="settings-control-cell slider-group">
                                    <input id="promptMaxTokens" type="range" min="1" max={promptModelMaxLimit || 1} step={promptModelMaxLimit > 1000 ? 50 : 1} value={promptMaxTokens} onChange={(e) => handlePromptLLMSettingChange('maxTokens', e.target.value)} disabled={!promptModelMaxLimit || promptModelMaxLimit <= 1} style={{ '--value-percent': `${promptTokenPercent}%` }} />
                                    <span>{promptMaxTokens ?? 'N/A'}</span>
                                    <small>(Model Max: {promptModelMaxLimit ? promptModelMaxLimit.toLocaleString() : 'N/A'})</small>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* --- START: New UI for Custom Agent Tools --- */}
                    <div className="form-section prompt-tools-section" style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px dashed #ccc' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                            <input type="checkbox" id="useCustomPromptTools" checked={useCustomPromptTools} onChange={(e) => setUseCustomPromptTools(e.target.checked)} style={{ marginRight: '10px', width: 'auto', height: 'auto' }} />
                            <label htmlFor="useCustomPromptTools" style={{ marginBottom: '0', fontWeight: 'normal' }}>Use custom Agent Tools for this prompt</label>
                        </div>
    
                        {useCustomPromptTools && (
                            <>
                                <div className="tools-group tools-columns" style={{ opacity: useCustomPromptTools ? 1 : 0.5, pointerEvents: useCustomPromptTools ? 'auto' : 'none' }}>
                                    {Object.entries(TOOL_GROUPS).map(([groupName, toolKeys]) => (
                                        <div className="tools-column" key={groupName}>
                                            <h4 style={{fontSize: '1em', color: 'var(--text-secondary)', marginBottom: '10px'}}>{groupName.charAt(0).toUpperCase() + groupName.slice(1)} tools</h4>
                                            <div className="tools-grid">
                                                {toolKeys.map(toolKey => (
                                                    <div className="setting-item checkbox tool-item" key={toolKey}>
                                                        <input
                                                            id={`prompt-tool-${toolKey}`}
                                                            type="checkbox"
                                                            checked={!!promptTools[toolKey]}
                                                            onChange={() => handlePromptToolChange(toolKey)}
                                                        />
                                                        <label htmlFor={`prompt-tool-${toolKey}`} title={TOOL_DESCRIPTIONS[toolKey] || toolKey}>
                                                            {toolKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Tool discovery toggle (visible only when custom tools are used) */}
                                <div className="setting-item checkbox" style={{ marginTop: '15px' }}>
                                    <input
                                        id="toolDiscoveryToggle"
                                        type="checkbox"
                                        checked={!!toolDiscovery}
                                        onChange={(e) => setToolDiscovery(e.target.checked)}
                                    />
                                    <label htmlFor="toolDiscoveryToggle" title="Allow the agent to discover tools itself">
                                        Allow tool discovery
                                    </label>
                                </div>

                                {/* Collections management UI (visible only when custom tools are used) */}
                                <div className="form-section" style={{ marginTop: '15px' }}>
                                    <h4 style={{fontSize: '1em', color: 'var(--text-secondary)', marginBottom: '10px'}}>Collections</h4>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <label htmlFor="availableCollections" style={{ marginBottom: 0 }}>Select:</label>
                                            <select
                                                id="availableCollections"
                                                value={selectedCollectionId}
                                                onChange={(e) => setSelectedCollectionId(e.target.value)}
                                                disabled={(availableCollections || []).length === 0}
                                            >
                                                {availableCollections.length === 0 ? (
                                                    <option value="">No collections available</option>
                                                ) : (
                                                    <>
                                                        <option value="">-- Select --</option>
                                                        {availableCollections.map(col => (
                                                            <option key={col.id} value={String(col.id)}>{col.name || col.id}</option>
                                                        ))}
                                                    </>
                                                )}
                                            </select>
                                            <button type="button" onClick={handleAddSelectedCollection} disabled={!selectedCollectionId}>Add selected</button>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <label htmlFor="manualCollectionId" style={{ marginBottom: 0 }}>Or enter ID:</label>
                                            <input
                                                id="manualCollectionId"
                                                type="text"
                                                placeholder="collection_id"
                                                value={manualCollectionId}
                                                onChange={(e) => setManualCollectionId(e.target.value)}
                                                style={{ width: '200px' }}
                                            />
                                            <button type="button" onClick={handleAddManualCollection} disabled={!manualCollectionId.trim()}>Add</button>
                                        </div>
                                    </div>

                                    {collectionIds.length > 0 && (
                                        <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            {collectionIds.map(id => {
                                                const match = availableCollections.find(c => String(c.id) === String(id));
                                                const label = match ? (match.name || match.id) : id;
                                                return (
                                                    <span key={id} className="tag" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'var(--bg-tertiary)', padding: '4px 8px', borderRadius: '12px' }}>
                                                        <span>{label}</span>
                                                        <button type="button" onClick={() => handleRemoveCollection(id)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)' }} title="Remove">×</button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Placeholder: when the associate endpoint exists, call it with { prompt_id, collection_ids } */}
                                    {/* const payload = prepareCollectionsPayload(); */}
                                </div>
                            </>
                        )}
                    </div>
                    {/* --- END: New UI for Custom Agent Tools --- */}
    
                    <div className="modal-button-group" style={{ marginTop: '20px' }}>
                        {/* Permission Gating: Only users with user:update-settings can create/update/delete prompts */}
                            {selectedPrompt && !isSystemPrompt && (
                                <ProtectedElement required={["prompts:update"]} behavior="disable">
                                <button onClick={() => {
                                    const llmPayload = prepareLLMSettingsPayload();
                                    const toolsPayload = prepareToolsPayload(); // Get tools payload
                                    const collectionsPayload = prepareCollectionsPayload();
                                    if (llmPayload) handleUpdatePrompt(selectedPrompt.id, internalPromptName, internalPromptText, internalPromptGroup, llmPayload, toolsPayload, collectionsPayload);
                                }} disabled={!canSubmit} className="primary">Update Prompt</button>
                                </ProtectedElement>
                            )}
                            <ProtectedElement required={["prompts:create"]} behavior="disable">
                            <button onClick={() => {
                                const llmPayload = prepareLLMSettingsPayload();
                                const toolsPayload = prepareToolsPayload(); // Get tools payload
                                const collectionsPayload = prepareCollectionsPayload();
                                if (llmPayload) handleCreatePrompt(internalPromptName, internalPromptText, internalPromptGroup, llmPayload, toolsPayload, collectionsPayload);
                            }} disabled={!canSubmit} className="primary">Save as New Prompt</button>
                            </ProtectedElement>
                            {selectedPrompt && !isSystemPrompt && (
                                <ProtectedElement required={["prompts:delete"]} behavior="disable">
                                    <button onClick={handleDeletePrompt} className="danger" title={`Delete prompt: ${selectedPrompt?.prompt_name}`}>Delete Prompt</button>
                                </ProtectedElement>
                            )}
                        {(selectedPrompt || internalPromptName || internalPromptText || useCustomPromptSettings || useCustomPromptTools) && (
                            <button onClick={onClearSelectedPrompt} className="cancel">Clear / New</button>
                        )}
                    </div>
                </div>
                </ProtectedElement>
            </div>
        );
    });
    
    // --- Pipeline Sub-Component (kept the same) ---
    const PipelineContent = React.memo(({
        selectedPipeline,
        pipelines = [],
        prompts = [],
        pipelinePrompts = [],
        handleSelectPipelineChange,
        selectedPrompt,
        handleSelectPromptChange,
        handleAddToPipeline,
        handleRemoveFromPipeline,
        handleSavePipeline,     // For "Save New Pipeline" (from scratch) AND "Save as New Copy"
        handleUpdatePipeline,   // For "Update My Pipeline" (user's own existing pipeline)
        handleDeletePipeline,   // For "Delete My Pipeline" - THIS IS THE PROP
        promptsLoading,
        pipelinesLoading,
        clearPipelineEditor
    }) => {
        const [internalPipelineName, setInternalPipelineName] = useState('');
    
        useEffect(() => {
            setInternalPipelineName(selectedPipeline?.name || '');
        }, [selectedPipeline]);
    
        // **DEBUGGING LOG 1: Monitor selectedPipeline in PipelineContent**
        useEffect(() => {
            console.log("PipelineContent: selectedPipeline state updated to:", selectedPipeline);
            if (selectedPipeline) {
                console.log("PipelineContent: selectedPipeline.username is:", selectedPipeline.username);
            }
        }, [selectedPipeline]);
    
        const currentPipelinePrompts = pipelinePrompts || [];
        const promptIdsForSave = currentPipelinePrompts.map(p => p.id);
        const canSubmit = internalPipelineName.trim() && promptIdsForSave.length > 0;
    
        const isUserOwnedPipelineSelected = useMemo(() => {
            const isOwned = selectedPipeline && 
                            selectedPipeline.username !== null && 
                            selectedPipeline.username !== '';
            // **DEBUGGING LOG 2: Monitor isUserOwnedPipelineSelected calculation**
            console.log(`PipelineContent: isUserOwnedPipelineSelected calculated: ${isOwned}. Based on selectedPipeline.username: '${selectedPipeline?.username}'`);
            return isOwned;
        }, [selectedPipeline]);
    
        const displayablePrompts = prompts || [];
    
        return (
            <div className="pipeline-content-area config-page-content">
                <ProtectedElement required={["pipelines:read"]} behavior="disabled" fallbackVariant={"overlay"}>
                <h3>Manage Prompt Pipelines</h3>
                <p>
                    Create new pipelines or edit your existing ones. 
                    You can load any pipeline (system or your own) into the editor, modify it, 
                    and then use "Save as New Copy" to create a new pipeline under your account.
                </p>
    
                <h4>
                    {selectedPipeline 
                        ? `Editor (Loaded: ${selectedPipeline.name}${selectedPipeline.username === null || selectedPipeline.username === '' ? ' [System]' : ''})`
                        : 'Create New Pipeline'}
                </h4>
    
                <div className="form-section">
                    <label htmlFor="pipelineNameInput">Pipeline Name (for save/copy):</label>
                    <input
                        id="pipelineNameInput"
                        type="text"
                        placeholder="Enter pipeline name"
                        value={internalPipelineName}
                        onChange={(e) => setInternalPipelineName(e.target.value)}
                    />
                </div>
    
                <div className="form-section">
                    <label htmlFor="pipelinePromptSelect">Add Prompt to Sequence:</label>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <select
                            id="pipelinePromptSelect"
                            onChange={handleSelectPromptChange}
                            value={selectedPrompt?.id || ''}
                            style={{ flexGrow: 1 }}
                            disabled={promptsLoading || displayablePrompts.length === 0}
                        >
                            <option value="">-- Select a prompt --</option>
                            {promptsLoading && <option value="" disabled>Loading prompts...</option>}
                            {displayablePrompts.slice().sort((a, b) => (a.prompt_name || '').localeCompare(b.prompt_name || '')).map(prompt => (
                                <option key={prompt.id} value={prompt.id}>
                                    {prompt.prompt_name} {prompt.username === null || prompt.username === '' ? '(System)' : (prompt.username ? '' : '')}
                                </option>
                            ))}
                        </select>
                        <button onClick={handleAddToPipeline} className="secondary" style={{ flexShrink: 0 }} disabled={!selectedPrompt}>Add</button>
                    </div>
                    {displayablePrompts.length === 0 && !promptsLoading && <small>No prompts available to add.</small>}
                </div>
    
                <h4>Current Pipeline Sequence (Order Matters)</h4>
                <ul className="pipeline-prompt-list">
                    {currentPipelinePrompts.map((prompt, index) => (
                        <li key={`${prompt.id}-${index}`} className="pipeline-prompt-item" data-index={index + 1}>
                            <span>{prompt.prompt_name} {prompt.username === null || prompt.username === '' ? '(System)' : (prompt.username ? '' : '')}</span>
                            <button onClick={() => handleRemoveFromPipeline(prompt)} className="remove-button" title="Remove">X</button>
                        </li>
                    ))}
                    {currentPipelinePrompts.length === 0 && <li className="empty-pipeline">No prompts added.</li>}
                </ul>
                {/* End of unchanged JSX parts */}
    
                <div className="modal-button-group">
                        {/* Case 1: Editor is for a brand new pipeline (no pipeline loaded) */}
                        {!selectedPipeline && (
                            <ProtectedElement required={["pipelines:create"]} behavior="hide">
                            <button
                                onClick={() => {
                                    console.log("PipelineContent: 'Save New Pipeline' button clicked.");
                                    handleSavePipeline(internalPipelineName, promptIdsForSave);
                                }}
                                disabled={!canSubmit}
                                className="primary"
                                title="Save the current editor content as a new pipeline for yourself."
                            >
                                Save New Pipeline
                            </button>
                            </ProtectedElement>
                        )}

                        {/* Case 2: A user-owned pipeline is loaded - Show "Update My Pipeline" */}
                        {selectedPipeline && isUserOwnedPipelineSelected && (
                            <ProtectedElement required={["pipelines:update"]} behavior="hide">
                            <button
                                onClick={() => {
                                    console.log("PipelineContent: 'Update My Pipeline' button clicked.");
                                    handleUpdatePipeline(selectedPipeline.id, internalPipelineName, promptIdsForSave);
                                }}
                                disabled={!canSubmit}
                                className="primary"
                                title="Update your existing pipeline with the current editor content."
                            >
                                Update My Pipeline
                            </button>
                            </ProtectedElement>
                        )}
                        
                        {/* "Save as New Copy" - available if any pipeline is loaded (system or user's own) */}
                        {selectedPipeline && (
                            <ProtectedElement required={["pipelines:create"]} behavior="hide">
                            <button
                                onClick={() => {
                                    console.log("PipelineContent: 'Save as New Copy' button clicked.");
                                    handleSavePipeline(internalPipelineName, promptIdsForSave);
                                }}
                                disabled={!canSubmit}
                                className="secondary" 
                                title="Create a new pipeline for yourself using the current editor content, regardless of the loaded pipeline's origin."
                            >
                                Save as New Copy
                            </button>
                            </ProtectedElement>
                        )}
                        
                        {/* Delete button for USER'S OWN pipelines */}
                        {console.log("PipelineContent: Rendering Delete button? Condition selectedPipeline && isUserOwnedPipelineSelected:", !!(selectedPipeline && isUserOwnedPipelineSelected))}
                        {selectedPipeline && isUserOwnedPipelineSelected && (
                            <ProtectedElement required={["pipelines:delete"]} behavior="hide">
                            <button
                                onClick={() => {
                                    console.log("PipelineContent: DELETE BUTTON CLICKED! Calling handleDeletePipeline prop. Selected Pipeline ID:", selectedPipeline?.id);
                                    handleDeletePipeline();
                                }}
                                className="danger"
                                title={`Delete pipeline: ${selectedPipeline.name}`}
                            >
                                Delete My Pipeline
                            </button>
                            </ProtectedElement>
                        )}
    
                    {/* Clear / New Button */}
                    {(selectedPipeline || internalPipelineName.trim() || currentPipelinePrompts.length > 0) && (
                        <button onClick={clearPipelineEditor} className="cancel">Clear Editor / New</button>
                    )}
                </div>
                </ProtectedElement>
            </div>
        );
    });

    // --- Other Settings Sub-Component (kept the same) ---
    const OtherSettingsContent = React.memo(({ currentTheme, setCurrentTheme, handleApiError }) => {
        const handleThemeChange = async (newTheme) => {
            // Do nothing if the theme is already set to the new value
            if (currentTheme === newTheme) return;
    
            const previousTheme = currentTheme;
            // Optimistically update the UI for a responsive feel
            setCurrentTheme(newTheme); 
    
            try {
                // Persist the change to the backend
                await axios.post('/api/user/theme', 
                    { theme: newTheme }, 
                    { withCredentials: true }
                );
            } catch (error) {
                // If the API call fails, revert the theme and show an error
                setCurrentTheme(previousTheme); 
                handleApiError(error, 'saving theme preference'); 
            }
        };
    
        return (
            <div className="other-settings-content config-page-content">
                <h3>Other Settings</h3>
                <p>Application-wide preferences and settings.</p>
                
                <h4>Appearance</h4>
                <div className="form-section">
                    <label>Theme</label>
                    <div className="theme-toggle-container">
                        <button 
                            className={`theme-button ${currentTheme === 'LIGHT' ? 'active' : ''}`}
                            onClick={() => handleThemeChange('LIGHT')}
                            aria-pressed={currentTheme === 'LIGHT'}
                        >
                            Light
                        </button>
                        <button 
                            className={`theme-button ${currentTheme === 'DARK' ? 'active' : ''}`}
                            onClick={() => handleThemeChange('DARK')}
                            aria-pressed={currentTheme === 'DARK'}
                        >
                            Dark
                        </button>
                    </div>
                </div>
            </div>
        );
    });

    const ProfileSettingsContent = React.memo(({ handleApiError }) => {
        const [password, setPassword] = useState('');
        const [confirmPassword, setConfirmPassword] = useState('');
        const [showPassword, setShowPassword] = useState(false);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [successMessage, setSuccessMessage] = useState('');
        const [errorMessage, setErrorMessage] = useState('');

        // Basic password requirements
        const requirements = useMemo(() => ([
            { id: 'length', label: 'At least 8 characters', test: (v) => v.length >= 8 },
            { id: 'upper', label: 'Contains an uppercase letter', test: (v) => /[A-Z]/.test(v) },
            { id: 'lower', label: 'Contains a lowercase letter', test: (v) => /[a-z]/.test(v) },
            { id: 'number', label: 'Contains a number', test: (v) => /\d/.test(v) },
            { id: 'special', label: 'Contains a special character', test: (v) => /[^A-Za-z0-9]/.test(v) },
            { id: 'spaces', label: 'No spaces', test: (v) => !/\s/.test(v) },
        ]), []);

        const allReqsPass = requirements.every(r => r.test(password));
        const passwordsMatch = password.length > 0 && password === confirmPassword;
        const canSubmit = allReqsPass && passwordsMatch && !isSubmitting;

        const clearMessages = () => {
            setSuccessMessage('');
            setErrorMessage('');
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            clearMessages();
            if (!canSubmit) return;
            setIsSubmitting(true);
            try {
                const res = await axios.post(
                    `${BASE_URL}/api/user/update_password`,
                    { password },
                    { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
                );
                setSuccessMessage(res?.data?.message || 'Password updated successfully.');
                setPassword('');
                setConfirmPassword('');
            } catch (error) {
                const msg = error?.response?.data?.error || 'Failed to update password.';
                setErrorMessage(msg);
                // Surface to top-level handler in Axios error shape
                handleApiError && handleApiError(error, 'Update Password');
            } finally {
                setIsSubmitting(false);
            }
        };

        return (
            <div className="profile-settings-content config-page-content">
                <ProtectedElement required={["user:update-settings"]} behavior="disabled" fallbackVariant={"overlay"}>
                    <h3>Profile</h3>
                    <p>Update your account password below. Choose a strong password that meets the requirements.</p>

                    {successMessage && (
                        <div className="success-message" role="status" style={{ marginBottom: '10px' }}>
                            {successMessage}
                        </div>
                    )}
                    {errorMessage && (
                        <div className="error-message" role="alert" style={{ marginBottom: '10px' }}>
                            {errorMessage}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="form-section" style={{ maxWidth: 480 }}>
                        <div className="form-section">
                            <label htmlFor="newPassword">New Password</label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <input
                                    id="newPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => { clearMessages(); setPassword(e.target.value); }}
                                    autoComplete="new-password"
                                    placeholder="Enter new password"
                                    aria-describedby="password-help"
                                />
                                <button type="button" className="secondary" onClick={() => setShowPassword(v => !v)} aria-pressed={showPassword}>
                                    {showPassword ? 'Hide' : 'Show'}
                                </button>
                            </div>
                            <small id="password-help">Must meet all requirements below.</small>
                        </div>

                        <div className="form-section">
                            <label htmlFor="confirmPassword">Confirm New Password</label>
                            <input
                                id="confirmPassword"
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => { clearMessages(); setConfirmPassword(e.target.value); }}
                                autoComplete="new-password"
                                placeholder="Re-enter new password"
                            />
                            {confirmPassword && !passwordsMatch && (
                                <small style={{ color: 'var(--danger)' }}>Passwords do not match.</small>
                            )}
                        </div>

                        <div className="form-section" style={{ marginTop: '8px' }}>
                            <h4 style={{ fontSize: '1em', color: 'var(--text-secondary)', margin: 0, marginBottom: '6px' }}>Password requirements</h4>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {requirements.map(req => (
                                    <li key={req.id} style={{ display: 'flex', alignItems: 'center', gap: 8, color: req.test(password) ? 'var(--success)' : 'var(--text-secondary)' }}>
                                        <span aria-hidden>{req.test(password) ? '✔️' : '○'}</span>
                                        <span>{req.label}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="modal-button-group" style={{ marginTop: '16px' }}>
                            <button type="submit" className="primary" disabled={!canSubmit}>{isSubmitting ? 'Updating…' : 'Update Password'}</button>
                            {(password || confirmPassword) && (
                                <button type="button" className="cancel" onClick={() => { setPassword(''); setConfirmPassword(''); clearMessages(); }}>
                                    Clear
                                </button>
                            )}
                        </div>
                    </form>
                </ProtectedElement>
            </div>
        );
    });


    // --- Main Component Return ---
    return (
            <div className="config-section-container">
            <div className="settings-page-container">
                <SettingsSidebar
                    activeTab={activeTab} setActiveTab={setActiveTab}
                    handleSelectPipeline={handleSelectPipelineChange} // Pass handler to load pipeline
                    pipelines={pipelines} // Pass list of pipelines
                    selectedPipeline={selectedPipeline} // Pass currently selected pipeline
                    isLoading={pipelinesLoading} // Pass loading state for pipeline list
                />
                <div className="settings-content-area">
                     {/* Display API errors */}
                     {apiError && (
                         <div className="error-message" style={{marginBottom: '15px'}} onClick={() => setApiError(null)} title="Click to dismiss">
                             {apiError}
                         </div>
                     )}
                     {/* Render content based on active tab */}
                    {activeTab === 'Main' && (
                         <MainContent
                             settings={settings}
                             providerModelMap={providerModelMap}
                             maxTokenValue={maxTokenValue}
                             handleSettingChange={handleSettingChange}
                             handleSliderChange={handleSliderChange}
                             handleSaveSettings={handleSaveSettings}
                             isSaving={isSaving} // Pass saving state
                         />
                    )}
                    {activeTab === 'Prompts' && (
                        <PromptManagementContent
                            prompts={prompts}
                            selectedPrompt={selectedPrompt}
                            handleSelectPromptChange={handleSelectPromptChange}
                            handleUpdatePrompt={handleUpdatePrompt}
                            handleCreatePrompt={handleCreatePrompt}
                            promptsLoading={promptsLoading}
                            clearSelectedPrompt={clearSelectedPrompt}
                            globalSettings={settings} 
                            providerModelMap={providerModelMap}
                            handleDeletePrompt={handleDeletePrompt} // <-- Add this new prop
                        />
                    )}
                    {activeTab === 'Prompt Pipelines' && (
                         <PipelineContent
                             selectedPipeline={selectedPipeline}
                             prompts={prompts} // Pass full prompts list
                             pipelinePrompts={pipelinePrompts} // Pass current editor's list of prompt objects
                             handleSelectPipelineChange={handleSelectPipelineChange} // For selecting a pipeline from dropdown
                             selectedPrompt={selectedPrompt} // Current prompt selected in the "add prompt" dropdown
                             handleSelectPromptChange={handleSelectPromptChange} // Handler for "add prompt" dropdown
                             handleAddToPipeline={handleAddToPipeline}
                             handleRemoveFromPipeline={handleRemoveFromPipeline}
                             handleSavePipeline={handleSavePipeline} // For new or "save as copy"
                             handleUpdatePipeline={handleUpdatePipeline} // For updating existing user-owned
                             handleDeletePipeline={handleDeletePipeline} // For deleting existing user-owned
                             promptsLoading={promptsLoading}
                             pipelinesLoading={pipelinesLoading}
                             clearPipelineEditor={clearPipelineEditor}
                         />
                     )}
                    {activeTab === 'Data Upload' && (
                         <FileUploadHandler
                             prompts={prompts} // Pass prompts list for selection
                             handleApiError={handleApiError} // Pass error handler
                             promptsLoading={promptsLoading} // Pass loading state
                         />
                     )}
                    {activeTab === 'Profile' && (
                        <ProfileSettingsContent handleApiError={handleApiError} />
                    )}
                    {activeTab === 'Other Settings' && 
                        <OtherSettingsContent 
                            currentTheme={theme} 
                            setCurrentTheme={setTheme} 
                            handleApiError={handleApiError}
                        />
                    }
                </div>
            </div>
            </div>
     );
};

export default SettingsPage;

